import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import { isSocio } from '../utils/permissions';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ClipboardList, UserPlus, Calendar, ChevronDown, ChevronRight, 
  Users, CheckCircle, FileText, Clock, ThumbsUp, RotateCcw, 
  MessageSquare, Filter, User, CalendarRange, Trash2, Plus, Search, Briefcase, AlertTriangle,
  Pencil, Send, Star, Paperclip, Key, RefreshCw, Flame, Zap, Info, Bell, File, Download
} from 'lucide-react';
import api, { formatErrorMessage, settleRequests } from '../utils/api';
import { toast } from 'sonner';
import TaskAttachments from '../components/TaskAttachments';
import MentionInput from '../components/MentionInput';
import ClientCredentials from '../components/ClientCredentials';
import HierarchicalCredentials from '../components/HierarchicalCredentials';

// Helper para obtener el estilo de prioridad
const getPriorityStyle = (priority) => {
  switch (priority) {
    case 1:
      return {
        label: 'Urgente',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        borderColor: 'border-red-400',
        icon: Flame,
        iconColor: 'text-red-500'
      };
    case 3:
      return {
        label: 'Baja',
        bgColor: 'bg-slate-100',
        textColor: 'text-slate-600',
        borderColor: 'border-slate-300',
        icon: null,
        iconColor: ''
      };
    default: // 2 = Regular
      return {
        label: 'Regular',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-200',
        icon: null,
        iconColor: ''
      };
  }
};

const TableroLideres = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [minutasJerarquicas, setMinutasJerarquicas] = useState([]);
  const [tareasPorRevisar, setTareasPorRevisar] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedTarea, setSelectedTarea] = useState(null);
  const [showAsignar, setShowAsignar] = useState(false);
  const [showRevisar, setShowRevisar] = useState(false);
  const [areaFilter, setAreaFilter] = useState('auto');
  const [usuariosAsignables, setUsuariosAsignables] = useState({ usuarios_area: [], otros_lideres: [], socios: [] });
  const [asignacion, setAsignacion] = useState({
    responsable: '',
    responsable_id: '',
    nuevo_plazo: '',
    priority: 2,  // Default: Regular
    comentario_inicial: ''
  });
  const [mencionesAsignacion, setMencionesAsignacion] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('asignar');
  
  // Estado para Seguimiento de Equipo
  const [funcionariosArea, setFuncionariosArea] = useState([]);
  const [selectedFuncionario, setSelectedFuncionario] = useState(null);
  const [tareasEquipo, setTareasEquipo] = useState({ pendientes: [], realizadas: [], enRevision: [] });
  const [fechaInicio, setFechaInicio] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [fechaFin, setFechaFin] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().split('T')[0];
  });
  
  // Estados para Vista por Cliente
  const [vistaMode, setVistaMode] = useState('funcionario'); // 'funcionario' o 'cliente'
  const [clientesConTareas, setClientesConTareas] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [tareasCliente, setTareasCliente] = useState({ pendientes: [], realizadas: [], enRevision: [], vencidas: [] });
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [filtroClienteSelect, setFiltroClienteSelect] = useState('');
  
  // Estado para eliminar tareas
  const [showEliminar, setShowEliminar] = useState(false);
  const [tareaAEliminar, setTareaAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [loadingEquipo, setLoadingEquipo] = useState(false);

  // Estado para crear tareas
  const [showCrearTarea, setShowCrearTarea] = useState(false);
  const [pspsActivas, setPspsActivas] = useState([]);
  const [pspSearch, setPspSearch] = useState('');
  const [loadingPsps, setLoadingPsps] = useState(false);
  const [selectedPsp, setSelectedPsp] = useState(null);
  const [nuevaTarea, setNuevaTarea] = useState({
    title: '',
    description: '',
    area: '',
    deadline: '',
    proposal_id: '',
    responsible: '',
    responsible_id: '',
    comentario_inicial: '',
    documentos_base: []
  });

  // Estado para crear tareas periódicas
  const [showCrearPeriodica, setShowCrearPeriodica] = useState(false);
  const [tareasPeriodicasSinAsignar, setTareasPeriodicasSinAsignar] = useState([]);
  const [clientesLista, setClientesLista] = useState([]);
  const [periodicaForm, setPeriodicaForm] = useState({
    titulo: '',
    descripcion: '',
    frecuencia: 'mensual',
    repeticiones_total: 12,
    fecha_inicio: '',
    dia_semana: null,
    dia_mes: 1,
    client_id: '',
    psp_id: '',
    asignado_id: '',
    sin_cliente: false
  });
  const [adjuntosPeriodica, setAdjuntosPeriodica] = useState([]);
  const [pspsCliente, setPspsCliente] = useState([]);
  const [creandoTarea, setCreandoTarea] = useState(false);
  const [mencionesNuevaTarea, setMencionesNuevaTarea] = useState([]);

  // Estado para editar tareas
  const [showEditar, setShowEditar] = useState(false);
  const [tareaAEditar, setTareaAEditar] = useState(null);
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    deadline: '',
    area: ''
  });
  const [editando, setEditando] = useState(false);

  // Estado para enviar a revisión de socios
  const [showEnviarSocios, setShowEnviarSocios] = useState(false);
  const [comentarioSocios, setComentarioSocios] = useState('');
  const [enviandoSocios, setEnviandoSocios] = useState(false);

  // Estado para archivos adjuntos en asignación/revisión
  const [adjuntosAsignacion, setAdjuntosAsignacion] = useState([]);
  const [adjuntosRevision, setAdjuntosRevision] = useState([]);
  
  // Estado para validación de plazo vs PSP
  const [plazoPspInfo, setPlazoPspInfo] = useState(null); // Info del plazo de la PSP
  const [plazoExcedePsp, setPlazoExcedePsp] = useState(false); // Si el plazo excede
  const [justificacionPlazo, setJustificacionPlazo] = useState(''); // Justificación obligatoria

  // Estado para modal de documentos en Seguimiento Equipo
  const [showDocsEquipo, setShowDocsEquipo] = useState(false);
  const [tareaDocsEquipo, setTareaDocsEquipo] = useState(null);

  // Estado para Seguimiento Equipo - Acciones avanzadas
  const [showBitacoraEquipo, setShowBitacoraEquipo] = useState(false);
  const [tareaBitacoraEquipo, setTareaBitacoraEquipo] = useState(null);
  const [showReasignarEquipo, setShowReasignarEquipo] = useState(false);
  const [tareaReasignarEquipo, setTareaReasignarEquipo] = useState(null);
  const [showEditarEquipo, setShowEditarEquipo] = useState(false);
  const [tareaEditarEquipo, setTareaEditarEquipo] = useState(null);
  const [editDataEquipo, setEditDataEquipo] = useState({ title: '', description: '', deadline: '', area: '' });
  const [reasignacionEquipo, setReasignacionEquipo] = useState({ responsable: '', responsable_id: '', nuevo_plazo: '', comentario: '' });
  const [procesandoEquipo, setProcesandoEquipo] = useState(false);

  // Estado para Tareas en Revisión
  const [tareasEnRevision, setTareasEnRevision] = useState([]);

  // Estado para modal de Credenciales
  const [showCredencialesEquipo, setShowCredencialesEquipo] = useState(false);
  const [tareaCredencialesEquipo, setTareaCredencialesEquipo] = useState(null);

  // Filtros de Seguimiento de Tareas
  const [filtroClienteEquipo, setFiltroClienteEquipo] = useState('');
  const [filtroPspEquipo, setFiltroPspEquipo] = useState('');
  const [filtroAreaSeguimiento, setFiltroAreaSeguimiento] = useState('mi_area'); // 'mi_area' o 'otras_areas'
  const [tareasOtrasAreas, setTareasOtrasAreas] = useState([]); // Tareas creadas por el líder en otras áreas

  // Función para contar adjuntos totales (directos + bitácora)
  const getTotalAdjuntos = (tarea) => {
    let total = 0;
    if (tarea.attachments?.length) total += tarea.attachments.length;
    if (tarea.gestiones?.length) {
      tarea.gestiones.forEach(g => {
        if (g.attachments?.length) total += g.attachments.length;
      });
    }
    return total;
  };

  // Filtrar tareas del equipo por cliente o PSP
  const filtrarTareasEquipo = (tareas) => {
    return tareas.filter(t => {
      // Filtro por cliente seleccionado (selector dinámico)
      const matchClienteSelect = !filtroClienteSelect || t.client_id === filtroClienteSelect;
      // Filtro por texto de cliente (mantener compatibilidad)
      const matchCliente = !filtroClienteEquipo || 
        (t.client_name?.toLowerCase().includes(filtroClienteEquipo.toLowerCase()));
      const matchPsp = !filtroPspEquipo || 
        (t.psp_folio?.toLowerCase().includes(filtroPspEquipo.toLowerCase()));
      return matchClienteSelect && matchCliente && matchPsp;
    });
  };

  // Verificar si el usuario puede eliminar una tarea
  // Regla: Líderes no pueden eliminar tareas que les fueron asignadas por otro líder/socio
  const puedoEliminarTarea = (tarea) => {
    if (!currentUser) return false;
    
    // Los socios pueden eliminar cualquier tarea
    if (isSocio(currentUser)) return true;
    
    // Para líderes: verificar si la tarea me fue asignada por otra persona
    const esAsignadoAMi = tarea.responsible_id === currentUser.id || tarea.responsible === currentUser.name;
    const creadorId = tarea.created_by_id;
    
    // Si me fue asignada por otra persona, no puedo eliminarla
    if (esAsignadoAMi && creadorId && creadorId !== currentUser.id) {
      return false;
    }
    
    return true;
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [areaFilter, currentUser]);

  // Cargar funcionarios del área cuando cambia el filtro de área
  useEffect(() => {
    if (currentUser) {
      const effectiveArea = getEffectiveAreaFilter();
      if (effectiveArea && effectiveArea !== 'all') {
        fetchFuncionariosArea(effectiveArea);
      }
    }
  }, [areaFilter, currentUser]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
      
      // Si es líder, establecer su área como filtro por defecto
      if (response.data.role === 'Líder' && response.data.areas?.length > 0) {
        setAreaFilter(response.data.areas[0]);
      }
    } catch (error) {
      toast.error('Error al cargar usuario');
    }
  };

  const getEffectiveAreaFilter = () => {
    // Si es líder y tiene áreas, forzar filtro por su área
    if (currentUser?.role === 'Líder' && currentUser?.areas?.length > 0) {
      if (areaFilter === 'auto' || areaFilter === 'all') {
        return currentUser.areas[0];
      }
      // Verificar que el filtro seleccionado esté en sus áreas
      if (!currentUser.areas.includes(areaFilter)) {
        return currentUser.areas[0];
      }
    }
    return areaFilter === 'auto' ? 'all' : areaFilter;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const effectiveArea = getEffectiveAreaFilter();
      const areaParam = effectiveArea !== 'all' ? `?area=${effectiveArea}` : '';
      
      const { values, fallidos } = await settleRequests({
        'tablero-lideres-jerarquico':  api.get(`/tasks/tablero-lideres-jerarquico${areaParam}`),
        'por-revisar':                 api.get(`/tasks/por-revisar${areaParam}`),
        'en-revision':                 api.get(`/tasks/en-revision${areaParam}`),
        'tareas-periodicas/sin-asignar': api.get('/tareas-periodicas/sin-asignar'),
      });

      if (values['tablero-lideres-jerarquico']) {
        const jd = values['tablero-lideres-jerarquico'].data;
        setMinutasJerarquicas(jd);
        // Expandir todos los grupos por defecto
        const expanded = {};
        jd.forEach((m, idx) => { expanded[idx] = true; });
        setExpandedGroups(expanded);
      }
      if (values['por-revisar'])            setTareasPorRevisar(values['por-revisar'].data);
      if (values['en-revision'])            setTareasEnRevision(values['en-revision'].data || []);
      if (values['tareas-periodicas/sin-asignar']) setTareasPeriodicasSinAsignar(values['tareas-periodicas/sin-asignar'].data || []);

      if (fallidos.length) {
        console.error('[TableroLideres] Endpoints con error:', fallidos);
        fallidos.forEach(f => toast.error(`Tablero · ${f.detalle}`, { duration: 8000 }));
      }

      // Cargar funcionarios del área para seguimiento
      if (effectiveArea !== 'all') {
        fetchFuncionariosArea(effectiveArea);
      }
    } catch (error) {
      console.error('[TableroLideres] Error inesperado:', error);
      toast.error('Error inesperado en el Tablero de Líderes');
    } finally {
      setLoading(false);
    }
  };

  const fetchFuncionariosArea = async (area) => {
    try {
      const response = await api.get(`/users/asignables?area=${area}`);
      setFuncionariosArea(response.data.usuarios_area || []);
    } catch (error) {
      console.error('Error al cargar funcionarios');
    }
  };

  // Cargar tareas creadas por el líder para otras áreas
  const fetchTareasOtrasAreas = async () => {
    try {
      const response = await api.get('/tasks/creadas-por-mi-otras-areas');
      setTareasOtrasAreas(response.data || []);
    } catch (error) {
      console.error('Error al cargar tareas de otras áreas');
      setTareasOtrasAreas([]);
    }
  };

  const fetchTareasEquipo = async (funcionarioId, funcionarioName) => {
    setLoadingEquipo(true);
    try {
      const [pendientesRes, realizadasRes] = await Promise.all([
        api.get('/tasks/mis-tareas'),
        api.get('/tasks/mis-tareas-realizadas')
      ]);
      
      // Filtrar por funcionario y fechas
      const filterByFuncionario = (tareas) => tareas.filter(t => 
        t.responsible_id === funcionarioId || t.responsible === funcionarioName
      );
      
      const filterByFecha = (tareas) => tareas.filter(t => {
        const fecha = new Date(t.created_at || t.fecha_aprobada);
        const inicio = new Date(fechaInicio);
        const fin = new Date(fechaFin);
        fin.setHours(23, 59, 59);
        return fecha >= inicio && fecha <= fin;
      });

      // Como admin/líder, necesitamos un endpoint diferente
      // Por ahora usamos los datos disponibles
      const effectiveArea = getEffectiveAreaFilter();
      const equipoRes = await api.get(`/tasks/equipo/${effectiveArea}`);
      
      const tareasUsuario = equipoRes.data.tareas_por_usuario?.[funcionarioName] || { pendientes: [], realizadas: [], enRevision: [] };
      
      setTareasEquipo({
        pendientes: tareasUsuario.pendientes || [],
        realizadas: filterByFecha(tareasUsuario.realizadas || []),
        enRevision: tareasUsuario.enRevision || []
      });
    } catch (error) {
      toast.error('Error al cargar tareas del equipo');
    } finally {
      setLoadingEquipo(false);
    }
  };

  const handleSelectFuncionario = (funcionario) => {
    setSelectedFuncionario(funcionario);
    fetchTareasEquipo(funcionario.id, funcionario.name);
  };

  // Función para cargar clientes que tienen tareas en el área
  const fetchClientesConTareas = async () => {
    setLoadingClientes(true);
    try {
      const effectiveArea = getEffectiveAreaFilter();
      const areaParam = effectiveArea && effectiveArea !== 'all' ? `?area=${effectiveArea}` : '';
      
      // Usar el nuevo endpoint que busca por área de la tarea, no por área del responsable
      const response = await api.get(`/tasks/clientes-con-tareas-area${areaParam}`);
      const data = response.data || [];
      
      // Transformar al formato esperado por el componente
      const clientesArray = data.map(cliente => ({
        id: cliente.id,
        name: cliente.name,
        company: cliente.company,
        rut: cliente.rut,
        tareas: {
          pendientes: cliente.tareas.pendientes + cliente.tareas.pendientes_asignar,
          realizadas: cliente.tareas.realizadas,
          enRevision: cliente.tareas.en_revision,
          vencidas: cliente.tareas.vencidas
        },
        tareas_detalle: cliente.tareas_detalle
      })).filter(c => c.id); // Filtrar clientes sin ID (sin_cliente)
      
      setClientesConTareas(clientesArray);
    } catch (error) {
      console.error('Error fetching clientes:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoadingClientes(false);
    }
  };

  // Función para cargar tareas de un cliente específico
  const fetchTareasCliente = async (clienteId, clienteName) => {
    setLoadingEquipo(true);
    try {
      const effectiveArea = getEffectiveAreaFilter();
      const areaParam = effectiveArea && effectiveArea !== 'all' ? `?area=${effectiveArea}` : '';
      
      // Usar el nuevo endpoint que busca por área de la tarea
      const response = await api.get(`/tasks/clientes-con-tareas-area${areaParam}`);
      const data = response.data || [];
      
      // Encontrar el cliente específico
      const clienteData = data.find(c => c.id === clienteId);
      
      if (!clienteData) {
        setTareasCliente({ pendientes: [], realizadas: [], enRevision: [], vencidas: [] });
        return;
      }
      
      // Clasificar las tareas del cliente
      const tareasDelCliente = { pendientes: [], realizadas: [], enRevision: [], vencidas: [] };
      
      for (const tarea of clienteData.tareas_detalle || []) {
        const status = tarea.status;
        const deadline = tarea.deadline ? new Date(tarea.deadline) : null;
        
        // Aplicar filtro de fechas si hay deadline
        if (deadline) {
          const inicio = new Date(fechaInicio);
          const fin = new Date(fechaFin);
          fin.setHours(23, 59, 59);
          if (deadline < inicio || deadline > fin) continue;
        }
        
        if (tarea.is_vencida) {
          tareasDelCliente.vencidas.push(tarea);
        } else if (['Aprobada', 'Completada'].includes(status)) {
          tareasDelCliente.realizadas.push(tarea);
        } else if (['En Revisión', 'Revisión Socios'].includes(status)) {
          tareasDelCliente.enRevision.push(tarea);
        } else {
          tareasDelCliente.pendientes.push(tarea);
        }
      }
      
      setTareasCliente(tareasDelCliente);
    } catch (error) {
      toast.error('Error al cargar tareas del cliente');
    } finally {
      setLoadingEquipo(false);
    }
  };

  const handleSelectCliente = (cliente) => {
    setSelectedCliente(cliente);
    fetchTareasCliente(cliente.id, cliente.name);
  };

  // Effect para cargar clientes cuando cambia el modo de vista o el área
  useEffect(() => {
    if (vistaMode === 'cliente' && currentUser) {
      fetchClientesConTareas();
      setSelectedCliente(null); // Reset cliente seleccionado al cambiar área
    }
  }, [vistaMode, areaFilter, currentUser]);

  const fetchUsuariosAsignables = async (area) => {
    try {
      const areaParam = area ? `?area=${area}` : '';
      const response = await api.get(`/users/asignables${areaParam}`);
      setUsuariosAsignables(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    }
  };

  const toggleGroupExpanded = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleOpenAsignar = async (tarea) => {
    setSelectedTarea(tarea);
    setAsignacion({ responsable: '', responsable_id: '', nuevo_plazo: '', comentario_inicial: '' });
    setMencionesAsignacion([]);
    setPlazoExcedePsp(false);
    setJustificacionPlazo('');
    fetchUsuariosAsignables(tarea.area);
    
    // Usar el deadline de la tarea específica como fecha límite de referencia
    // Este deadline fue calculado originalmente basándose en el plazo_dias de la minuta
    if (tarea.deadline) {
      const fechaLimiteTarea = new Date(tarea.deadline);
      setPlazoPspInfo({
        tipo: 'tarea',
        titulo: tarea.title,
        fecha_limite: fechaLimiteTarea,
        fecha_original: tarea.deadline
      });
    } else {
      setPlazoPspInfo(null);
    }
    
    setShowAsignar(true);
  };

  // Validar si el plazo seleccionado excede el deadline original de la tarea
  const validarPlazoVsPsp = (fechaSeleccionada) => {
    if (!fechaSeleccionada || !plazoPspInfo?.fecha_limite) {
      setPlazoExcedePsp(false);
      return false;
    }
    
    const fechaPlazo = new Date(fechaSeleccionada);
    const fechaLimite = new Date(plazoPspInfo.fecha_limite);
    
    const excede = fechaPlazo > fechaLimite;
    setPlazoExcedePsp(excede);
    
    if (!excede) {
      setJustificacionPlazo(''); // Limpiar justificación si ya no excede
    }
    
    return excede;
  };

  // Handler para cambio de fecha con validación
  const handleCambioFechaPlazo = (fecha) => {
    setAsignacion(prev => ({ ...prev, nuevo_plazo: fecha }));
    validarPlazoVsPsp(fecha);
  };

  const handleSelectUsuario = (userId) => {
    const allUsers = [...usuariosAsignables.usuarios_area, ...usuariosAsignables.otros_lideres, ...usuariosAsignables.socios];
    const user = allUsers.find(u => u.id === userId);
    if (user) {
      setAsignacion(prev => ({
        ...prev,
        responsable: user.name,
        responsable_id: user.id
      }));
    }
  };

  const handleAsignarTarea = async () => {
    if (!selectedTarea || !asignacion.responsable) {
      toast.error('Seleccione un funcionario');
      return;
    }
    
    // Fecha de vencimiento es OBLIGATORIA
    if (!asignacion.nuevo_plazo) {
      toast.error('La fecha de vencimiento es obligatoria al asignar una tarea');
      return;
    }
    
    // Validación: Si el plazo excede la PSP, requiere justificación
    if (plazoExcedePsp && !justificacionPlazo.trim()) {
      toast.error('Debe justificar por qué el plazo excede el compromiso de la PSP');
      return;
    }

    setSubmitting(true);
    try {
      let url = `/tasks/${selectedTarea.id}/asignar?responsable=${encodeURIComponent(asignacion.responsable)}`;
      
      if (asignacion.responsable_id) {
        url += `&responsable_id=${asignacion.responsable_id}`;
      }
      
      url += `&nuevo_plazo=${new Date(asignacion.nuevo_plazo).toISOString()}`;
      
      // Incluir prioridad
      url += `&priority=${asignacion.priority || 2}`;

      // Incluir adjuntos si existen
      const attachmentIds = (adjuntosAsignacion || [])
        .filter(a => a && a.id)
        .map(a => a.id);
      if (attachmentIds.length > 0) {
        url += `&attachments=${encodeURIComponent(JSON.stringify(attachmentIds))}`;
      }

      // Incluir comentario inicial si existe (combinado con justificación si aplica)
      let comentarioFinal = asignacion.comentario_inicial?.trim() || '';
      if (plazoExcedePsp && justificacionPlazo.trim()) {
        const fechaLimite = plazoPspInfo?.fecha_limite ? new Date(plazoPspInfo.fecha_limite).toLocaleDateString('es-CL') : 'N/A';
        const alertaPlazo = `⚠️ ALERTA PLAZO: El plazo asignado excede el compromiso original de esta tarea (límite: ${fechaLimite}). Justificación: ${justificacionPlazo.trim()}`;
        comentarioFinal = comentarioFinal ? `${comentarioFinal}\n\n${alertaPlazo}` : alertaPlazo;
      }
      
      if (comentarioFinal) {
        url += `&comentario_inicial=${encodeURIComponent(comentarioFinal)}`;
      }

      await api.put(url);

      // Notificar menciones si las hay
      if (mencionesAsignacion.length > 0 && asignacion.comentario_inicial) {
        try {
          await api.post('/menciones/notificar', {
            mencionados: mencionesAsignacion.map(m => m.id),
            contexto: `tarea asignada "${selectedTarea.title}"`,
            mensaje: asignacion.comentario_inicial,
            url_contexto: '/mis-tareas',
            task_id: selectedTarea.id
          });
        } catch (e) {
          console.error('Error notificando menciones:', e);
        }
      }

      toast.success(`Tarea asignada a ${asignacion.responsable}`);
      setShowAsignar(false);
      setSelectedTarea(null);
      setAsignacion({ responsable: '', responsable_id: '', nuevo_plazo: '', priority: 2, comentario_inicial: '' });
      setMencionesAsignacion([]);
      setAdjuntosAsignacion([]);
      setPlazoPspInfo(null);
      setPlazoExcedePsp(false);
      setJustificacionPlazo('');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenRevisar = (tarea) => {
    setSelectedTarea(tarea);
    setFeedback('');
    setShowRevisar(true);
  };

  const handleAprobar = async () => {
    if (!selectedTarea || !feedback.trim()) {
      toast.error('El feedback es obligatorio para la auditoría');
      return;
    }

    setSubmitting(true);
    try {
      // Incluir adjuntos si existen
      const attachmentIds = (adjuntosRevision || [])
        .filter(a => a && a.id)
        .map(a => a.id);
      
      let url = `/tasks/${selectedTarea.id}/aprobar?feedback=${encodeURIComponent(feedback)}`;
      if (attachmentIds.length > 0) {
        url += `&attachments=${encodeURIComponent(JSON.stringify(attachmentIds))}`;
      }
      
      await api.put(url);
      toast.success('Tarea aprobada - Feedback guardado en bitácora');
      setShowRevisar(false);
      setSelectedTarea(null);
      setFeedback('');
      setAdjuntosRevision([]);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRechazar = async () => {
    if (!selectedTarea || !feedback.trim()) {
      toast.error('El feedback es obligatorio para la auditoría');
      return;
    }

    setSubmitting(true);
    try {
      // Incluir adjuntos si existen
      const attachmentIds = (adjuntosRevision || [])
        .filter(a => a && a.id)
        .map(a => a.id);
      
      let url = `/tasks/${selectedTarea.id}/rechazar?feedback=${encodeURIComponent(feedback)}`;
      if (attachmentIds.length > 0) {
        url += `&attachments=${encodeURIComponent(JSON.stringify(attachmentIds))}`;
      }
      
      await api.put(url);
      toast.success('Tarea devuelta - Feedback guardado en bitácora');
      setShowRevisar(false);
      setSelectedTarea(null);
      setFeedback('');
      setAdjuntosRevision([]);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  // Función para enviar a revisión de socios
  const handleEnviarSocios = async () => {
    if (!selectedTarea || !comentarioSocios.trim()) {
      toast.error('El comentario es obligatorio');
      return;
    }

    setEnviandoSocios(true);
    try {
      await api.put(`/tasks/${selectedTarea.id}/enviar-revision-socios?comentario=${encodeURIComponent(comentarioSocios)}`);
      toast.success('Tarea enviada a revisión de Socios');
      setShowEnviarSocios(false);
      setShowRevisar(false);
      setSelectedTarea(null);
      setComentarioSocios('');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setEnviandoSocios(false);
    }
  };

  // Función para eliminar tarea
  const handleEliminarTarea = async () => {
    if (!tareaAEliminar) return;
    
    setEliminando(true);
    try {
      await api.delete(`/tasks/${tareaAEliminar.id}`);
      toast.success('Tarea eliminada correctamente');
      setShowEliminar(false);
      setTareaAEliminar(null);
      // Refrescar datos
      fetchData();
      if (selectedFuncionario) {
        fetchTareasEquipo(selectedFuncionario);
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setEliminando(false);
    }
  };

  // Función para abrir modal de edición
  const handleOpenEditar = (tarea) => {
    setTareaAEditar(tarea);
    setEditData({
      title: tarea.title || '',
      description: tarea.description || '',
      deadline: tarea.deadline ? new Date(tarea.deadline).toISOString().split('T')[0] : '',
      area: tarea.area || ''
    });
    setShowEditar(true);
  };

  // Función para guardar edición
  const handleGuardarEdicion = async () => {
    if (!tareaAEditar) return;
    
    setEditando(true);
    try {
      // Construir el objeto con los campos que cambiaron
      const updatePayload = {};
      
      if (editData.title !== tareaAEditar.title) updatePayload.title = editData.title;
      if (editData.description !== tareaAEditar.description) updatePayload.description = editData.description;
      if (editData.deadline) updatePayload.deadline = new Date(editData.deadline).toISOString();
      if (editData.area !== tareaAEditar.area) updatePayload.area = editData.area;
      
      if (Object.keys(updatePayload).length === 0) {
        toast.info('No hay cambios para guardar');
        setShowEditar(false);
        return;
      }
      
      // Enviar como body JSON en lugar de query params
      await api.put(`/tasks/${tareaAEditar.id}/editar`, updatePayload);
      toast.success('Tarea actualizada correctamente');
      setShowEditar(false);
      setTareaAEditar(null);
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setEditando(false);
    }
  };

  // Funciones para crear tareas
  const fetchPspsActivas = async (search = '') => {
    setLoadingPsps(true);
    try {
      const response = await api.get(`/proposals/activas-busqueda?search=${encodeURIComponent(search)}`);
      setPspsActivas(response.data);
    } catch (error) {
      console.error('Error al cargar PSPs');
      setPspsActivas([]);
    } finally {
      setLoadingPsps(false);
    }
  };

  // Cargar PSPs y usuarios asignables cuando se abre el modal de crear tarea
  useEffect(() => {
    if (showCrearTarea) {
      fetchPspsActivas(pspSearch);
      // Cargar usuarios asignables del área del líder
      const effectiveArea = getEffectiveAreaFilter();
      if (effectiveArea && effectiveArea !== 'all') {
        fetchUsuariosAsignables(effectiveArea);
      }
    }
  }, [showCrearTarea, pspSearch]);

  const handleSelectPsp = (psp) => {
    setSelectedPsp(psp);
    setNuevaTarea({ ...nuevaTarea, proposal_id: psp.id });
    setPspSearch('');
  };

  const handleCrearTarea = async () => {
    // Validaciones
    if (!nuevaTarea.proposal_id) {
      toast.error('Debe seleccionar un Cliente/PSP');
      return;
    }
    if (!nuevaTarea.title.trim() || !nuevaTarea.description.trim() || !nuevaTarea.area || !nuevaTarea.deadline) {
      toast.error('Complete todos los campos requeridos');
      return;
    }

    setCreandoTarea(true);
    try {
      await api.post('/tasks/crear-lider', {
        title: nuevaTarea.title,
        description: nuevaTarea.description,
        area: nuevaTarea.area,
        deadline: new Date(nuevaTarea.deadline).toISOString(),
        proposal_id: nuevaTarea.proposal_id,
        responsible: nuevaTarea.responsible || null,
        responsible_id: nuevaTarea.responsible_id || null,
        comentario_inicial: nuevaTarea.comentario_inicial || null
      });
      
      // Notificar menciones si las hay
      if (mencionesNuevaTarea.length > 0 && nuevaTarea.comentario_inicial) {
        try {
          await api.post('/menciones/notificar', {
            mencionados: mencionesNuevaTarea.map(m => m.id),
            contexto: `nueva tarea "${nuevaTarea.title}"`,
            mensaje: nuevaTarea.comentario_inicial,
            url_contexto: '/mis-tareas'
          });
        } catch (e) {
          console.error('Error notificando menciones:', e);
        }
      }
      
      toast.success('Tarea creada exitosamente');
      setShowCrearTarea(false);
      setNuevaTarea({
        title: '',
        description: '',
        area: '',
        deadline: '',
        proposal_id: '',
        responsible: '',
        responsible_id: '',
        comentario_inicial: '',
        documentos_base: []
      });
      setMencionesNuevaTarea([]);
      setSelectedPsp(null);
      setPspSearch('');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setCreandoTarea(false);
    }
  };

  const fetchClientesLista = async () => {
    try {
      const response = await api.get('/clients');
      setClientesLista(response.data || []);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const handleCrearPeriodicaLider = async () => {
    if (!periodicaForm.titulo.trim()) {
      toast.error('El título es obligatorio');
      return;
    }
    if (!periodicaForm.fecha_inicio) {
      toast.error('La fecha de inicio es obligatoria');
      return;
    }
    if (!periodicaForm.sin_cliente && !periodicaForm.client_id) {
      toast.error('Debe seleccionar un cliente o marcar "Sin cliente asociado"');
      return;
    }

    try {
      const payload = {
        ...periodicaForm,
        client_id: periodicaForm.sin_cliente ? null : periodicaForm.client_id,
        psp_id: periodicaForm.sin_cliente ? null : periodicaForm.psp_id,
        asignado_id: periodicaForm.asignado_id || null, // Puede ser null (sin asignar)
        repeticiones_total: parseInt(periodicaForm.repeticiones_total) || 1,
        attachment_ids: adjuntosPeriodica.map(a => a.id).filter(Boolean)
      };
      delete payload.sin_cliente;
      
      await api.post('/tareas-periodicas', payload);
      toast.success(periodicaForm.asignado_id ? 'Tarea periódica creada y asignada exitosamente' : 'Tarea periódica creada (pendiente de asignar)');
      setShowCrearPeriodica(false);
      setPeriodicaForm({
        titulo: '',
        descripcion: '',
        frecuencia: 'mensual',
        repeticiones_total: 12,
        fecha_inicio: '',
        dia_semana: null,
        dia_mes: 1,
        client_id: '',
        psp_id: '',
        asignado_id: '',
        sin_cliente: false
      });
      setAdjuntosPeriodica([]);
      setPspsCliente([]);
      // Refrescar lista de tareas por asignar
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  // Filtrar PSPs por cliente seleccionado
  const handleClienteChangeLider = async (clientId) => {
    setPeriodicaForm(prev => ({ ...prev, client_id: clientId, psp_id: '' }));
    if (clientId) {
      try {
        // Obtener todas las PSPs y filtrar por client_id y estados activos
        const response = await api.get(`/proposals?client_id=${clientId}`);
        const pspsActivas = (response.data || []).filter(p => 
          ['Aceptada', 'Ejecución/Activa'].includes(p.status)
        );
        setPspsCliente(pspsActivas);
        
        // Sugerir fecha de inicio basada en la minuta más reciente del cliente
        try {
          const minutasRes = await api.get(`/meetings/disponibles-para-psp?client_id=${clientId}`);
          const minutas = minutasRes.data || [];
          if (minutas.length > 0) {
            // Encontrar la minuta más reciente
            const minutaReciente = minutas.reduce((latest, minuta) => {
              const currentDate = new Date(minuta.created_at);
              const latestDate = new Date(latest.created_at);
              return currentDate > latestDate ? minuta : latest;
            });
            
            // Si la fecha de inicio está vacía, sugerirla
            if (!periodicaForm.fecha_inicio && minutaReciente) {
              const fechaSugerida = new Date(minutaReciente.created_at);
              // Ajustar al siguiente mes para tareas periódicas
              fechaSugerida.setMonth(fechaSugerida.getMonth() + 1);
              const fechaStr = fechaSugerida.toISOString().split('T')[0];
              setPeriodicaForm(prev => ({ 
                ...prev, 
                client_id: clientId, 
                psp_id: '',
                fecha_inicio: fechaStr + 'T09:00:00Z'
              }));
              toast.info(`Fecha sugerida basada en minuta del ${new Date(minutaReciente.created_at).toLocaleDateString('es-CL')}`);
            }
          }
        } catch (e) {
          // No es crítico, solo no sugerimos fecha
          console.log('No se pudo obtener minutas para sugerir fecha');
        }
      } catch (error) {
        setPspsCliente([]);
      }
    } else {
      setPspsCliente([]);
    }
  };

  const getFrecuenciaLabel = (frecuencia) => {
    const labels = { diaria: 'Diaria', semanal: 'Semanal', mensual: 'Mensual' };
    return labels[frecuencia] || frecuencia;
  };

  const handleViewPDF = async (meetingId) => {
    if (!meetingId) {
      toast.error('No hay minuta asociada');
      return;
    }
    try {
      const response = await api.post(`/meetings/${meetingId}/generate-pdf`, null, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Error al generar PDF');
    }
  };

  // === Funciones para Seguimiento de Equipo ===
  
  // Abrir modal de bitácora completa
  const handleOpenBitacoraEquipo = (tarea) => {
    setTareaBitacoraEquipo(tarea);
    setShowBitacoraEquipo(true);
  };

  // Abrir modal de credenciales desde seguimiento
  const handleOpenCredencialesEquipo = (tarea) => {
    setTareaCredencialesEquipo({
      client_name: tarea.client_name || tarea.cliente_nombre,
      cliente_nombre: tarea.client_name || tarea.cliente_nombre,
      client_id: tarea.client_id,
      client_rut: tarea.client_rut,
      client_clave_sii: tarea.client_clave_sii,
      client_clave_unica: tarea.client_clave_unica,
      client_empresas: tarea.client_empresas || []
    });
    setShowCredencialesEquipo(true);
  };

  // Descargar archivo adjunto de la bitácora
  const handleDownloadBitacoraFile = async (fileId, fileName) => {
    try {
      const response = await api.get(`/files/${fileId}`, {
        responseType: 'blob'
      });
      
      const contentType = response.headers?.['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'archivo';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      try {
        window.open(`/api/files/${fileId}`, '_blank');
      } catch (fallbackError) {
        toast.error('Error al descargar archivo');
      }
    }
  };

  // Abrir modal de edición desde seguimiento
  const handleOpenEditarEquipo = (tarea) => {
    setTareaEditarEquipo(tarea);
    setEditDataEquipo({
      title: tarea.title || '',
      description: tarea.description || '',
      deadline: tarea.deadline ? new Date(tarea.deadline).toISOString().split('T')[0] : '',
      area: tarea.area || ''
    });
    setShowEditarEquipo(true);
  };

  // Guardar edición desde seguimiento
  const handleGuardarEdicionEquipo = async () => {
    if (!tareaEditarEquipo) return;
    
    setProcesandoEquipo(true);
    try {
      // Construir el objeto con los campos que cambiaron
      const updatePayload = {};
      
      if (editDataEquipo.title !== tareaEditarEquipo.title) updatePayload.title = editDataEquipo.title;
      if (editDataEquipo.description !== tareaEditarEquipo.description) updatePayload.description = editDataEquipo.description;
      if (editDataEquipo.deadline) updatePayload.deadline = new Date(editDataEquipo.deadline).toISOString();
      if (editDataEquipo.area !== tareaEditarEquipo.area) updatePayload.area = editDataEquipo.area;
      
      if (Object.keys(updatePayload).length === 0) {
        toast.info('No hay cambios para guardar');
        setShowEditarEquipo(false);
        return;
      }
      
      // Enviar como body JSON en lugar de query params
      await api.put(`/tasks/${tareaEditarEquipo.id}/editar`, updatePayload);
      toast.success('Tarea actualizada correctamente');
      setShowEditarEquipo(false);
      setTareaEditarEquipo(null);
      // Refrescar datos del funcionario seleccionado
      if (selectedFuncionario) {
        fetchTareasEquipo(selectedFuncionario.id, selectedFuncionario.name);
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setProcesandoEquipo(false);
    }
  };

  // Abrir modal de reasignación
  const handleOpenReasignarEquipo = (tarea) => {
    setTareaReasignarEquipo(tarea);
    setReasignacionEquipo({
      responsable: '',
      responsable_id: '',
      nuevo_plazo: tarea.deadline ? new Date(tarea.deadline).toISOString().split('T')[0] : '',
      comentario: ''
    });
    fetchUsuariosAsignables(tarea.area);
    setShowReasignarEquipo(true);
  };

  // Ejecutar reasignación
  const handleReasignarEquipo = async () => {
    if (!tareaReasignarEquipo || !reasignacionEquipo.responsable_id) {
      toast.error('Seleccione un funcionario');
      return;
    }
    if (!reasignacionEquipo.nuevo_plazo) {
      toast.error('La fecha de vencimiento es obligatoria');
      return;
    }

    setProcesandoEquipo(true);
    try {
      let url = `/tasks/${tareaReasignarEquipo.id}/reasignar?`;
      url += `responsable=${encodeURIComponent(reasignacionEquipo.responsable)}`;
      url += `&responsable_id=${reasignacionEquipo.responsable_id}`;
      url += `&nuevo_plazo=${new Date(reasignacionEquipo.nuevo_plazo).toISOString()}`;
      if (reasignacionEquipo.comentario) {
        url += `&comentario=${encodeURIComponent(reasignacionEquipo.comentario)}`;
      }

      await api.put(url);
      toast.success(`Tarea reasignada a ${reasignacionEquipo.responsable}`);
      setShowReasignarEquipo(false);
      setTareaReasignarEquipo(null);
      // Refrescar datos
      if (selectedFuncionario) {
        fetchTareasEquipo(selectedFuncionario.id, selectedFuncionario.name);
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setProcesandoEquipo(false);
    }
  };

  // Seleccionar usuario para reasignación
  const handleSelectUsuarioReasignar = (userId) => {
    const allUsers = [...usuariosAsignables.usuarios_area, ...usuariosAsignables.otros_lideres, ...usuariosAsignables.socios];
    const user = allUsers.find(u => u.id === userId);
    if (user) {
      setReasignacionEquipo(prev => ({
        ...prev,
        responsable: user.name,
        responsable_id: user.id
      }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pendiente de Asignar': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Pendiente': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'En Progreso': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'Completada': return 'bg-green-100 text-green-700 border-green-200';
      case 'Aprobada': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  // Agrupar tareas por Cliente-Minuta/PSP
  const getTareasPorAsignarAgrupadas = () => {
    const grupos = {};
    minutasJerarquicas.forEach(minuta => {
      minuta.tareas?.forEach(t => {
        if (t.status === 'Pendiente de Asignar') {
          const key = `${minuta.cliente_nombre}_${minuta.psp_id}`;
          if (!grupos[key]) {
            grupos[key] = {
              cliente: minuta.cliente_nombre,
              psp_folio: minuta.folio_psp,  // Corregido: usar folio_psp del backend
              minutas: minuta.minutas_asociadas || [],
              meeting_id: minuta.minutas_asociadas?.[0]?.id,
              // Credenciales jerárquicas del cliente
              client_id: minuta.client_id,
              client_rut: minuta.client_rut,
              client_clave_sii: minuta.client_clave_sii,
              client_clave_unica: minuta.client_clave_unica,
              client_empresas: minuta.client_empresas || [],
              tareas: []
            };
          }
          grupos[key].tareas.push(t);
        }
      });
    });
    return Object.values(grupos);
  };

  const getTareasAsignadasAgrupadas = () => {
    const grupos = {};
    minutasJerarquicas.forEach(minuta => {
      minuta.tareas?.forEach(t => {
        if (t.status !== 'Pendiente de Asignar') {
          const key = `${minuta.cliente_nombre}_${minuta.psp_id}`;
          if (!grupos[key]) {
            grupos[key] = {
              cliente: minuta.cliente_nombre,
              psp_folio: minuta.folio_psp,  // Corregido: usar folio_psp del backend
              minutas: minuta.minutas_asociadas || [],
              meeting_id: minuta.minutas_asociadas?.[0]?.id,
              // Credenciales jerárquicas del cliente
              client_id: minuta.client_id,
              client_rut: minuta.client_rut,
              client_clave_sii: minuta.client_clave_sii,
              client_clave_unica: minuta.client_clave_unica,
              client_empresas: minuta.client_empresas || [],
              tareas: []
            };
          }
          grupos[key].tareas.push(t);
        }
      });
    });
    return Object.values(grupos);
  };

  const getTareasPorRevisarAgrupadas = () => {
    const grupos = {};
    tareasPorRevisar.forEach(t => {
      const key = `${t.client_name || 'Sin Cliente'}_${t.proposal_id || 'sin_psp'}`;
      if (!grupos[key]) {
        grupos[key] = {
          cliente: t.client_name || 'Sin Cliente',
          psp_folio: t.psp_folio,
          meeting_id: t.meeting_id,
          // Credenciales jerárquicas del cliente
          client_id: t.client_id,
          client_rut: t.client_rut,
          client_clave_sii: t.client_clave_sii,
          client_clave_unica: t.client_clave_unica,
          client_empresas: t.client_empresas || [],
          tareas: []
        };
      }
      grupos[key].tareas.push(t);
    });
    return Object.values(grupos);
  };

  const gruposPorAsignar = getTareasPorAsignarAgrupadas();
  const gruposAsignadas = getTareasAsignadasAgrupadas();
  const gruposPorRevisar = getTareasPorRevisarAgrupadas();

  const totalPorAsignar = gruposPorAsignar.reduce((acc, g) => acc + g.tareas.length, 0) + tareasPeriodicasSinAsignar.length;

  // Renderizar grupo jerárquico
  const renderGrupoJerarquico = (grupo, idx, tipo) => {
    const key = `${tipo}_${idx}`;
    const isExpanded = expandedGroups[key] !== false;
    
    return (
      <div key={key} className="mb-4">
        {/* Elemento Padre */}
        <div 
          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-slate-100 ${
            tipo === 'revisar' ? 'bg-green-50' : tipo === 'asignar' ? 'bg-amber-50' : 'bg-slate-50'
          }`}
          onClick={() => toggleGroupExpanded(key)}
        >
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-slate-600" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-600" />
          )}
          <Users className="w-5 h-5 text-primary" />
          <span className="font-semibold text-slate-800">{grupo.cliente}</span>
          
          {/* Mostrar PSP junto al nombre del cliente */}
          {grupo.psp_folio && (
            <>
              <span className="text-slate-400">-</span>
              <span className="text-primary font-medium text-sm">PSP N° {grupo.psp_folio}</span>
            </>
          )}
          
          {/* Ícono PDF para Minuta */}
          {(grupo.minutas?.length > 0 || grupo.meeting_id) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const minutaId = grupo.minutas?.[0]?.id || grupo.meeting_id;
                handleViewPDF(minutaId);
              }}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline ml-2"
              title="Ver PDF de la Minuta"
            >
              <FileText className="w-4 h-4" />
              <span className="text-sm">Ver Minuta</span>
            </button>
          )}
          
          <span className={`ml-auto px-2 py-1 rounded text-xs font-medium ${
            tipo === 'revisar' ? 'bg-green-200 text-green-800' 
            : tipo === 'asignar' ? 'bg-amber-200 text-amber-800' 
            : 'bg-primary/20 text-primary'
          }`}>
            {grupo.tareas.length} tarea{grupo.tareas.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Tareas del grupo */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="space-y-2 mt-2"
            >
              {grupo.tareas.map(tarea => (
                <Card 
                  key={tarea.id} 
                  className={`ml-6 border-l-4 ${
                    tipo === 'revisar' ? 'border-l-green-500' 
                    : tipo === 'asignar' ? 'border-l-amber-500' 
                    : 'border-l-primary'
                  }`}
                  data-testid={`tarea-${tipo}-${tarea.id}`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(tarea.status)}`}>
                            {tarea.status}
                          </span>
                          <span className="px-2 py-1 rounded text-xs bg-primary/10 text-primary">
                            {tarea.area}
                          </span>
                        </div>
                        <h4 className="font-semibold text-slate-900 mb-1">{tarea.title}</h4>
                        <p className="text-sm text-slate-600 mb-2">{tarea.description}</p>
                        
                        {/* Bitácora resumida para revisión */}
                        {tipo === 'revisar' && tarea.gestiones?.length > 0 && (
                          <div className="bg-slate-50 rounded-lg p-3 mb-3 max-h-32 overflow-y-auto">
                            <p className="text-xs font-medium text-slate-700 mb-2">Bitácora:</p>
                            {tarea.gestiones.slice(-3).map((g, idx) => (
                              <div key={idx} className="text-xs text-slate-600 mb-1">
                                <span className="font-medium">{g.usuario}</span>: {g.descripcion.substring(0, 100)}...
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          {tarea.responsible && tarea.responsible !== 'Por asignar' && (
                            <span className="flex items-center gap-1 font-medium text-slate-700">
                              <User className="w-4 h-4" />
                              {tarea.responsible}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Vence: {new Date(tarea.deadline).toLocaleDateString('es-CL')}
                          </span>
                          {tarea.gestiones?.length > 0 && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <MessageSquare className="w-4 h-4" />
                              {tarea.gestiones.length} gestiones
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Botones de acción */}
                      <div className="flex gap-2">
                        {/* Botón Editar - visible siempre para Líderes/Coordinadores */}
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-slate-600 hover:text-primary hover:bg-primary/10"
                          onClick={() => handleOpenEditar(tarea)}
                          data-testid={`editar-tarea-${tarea.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {tipo === 'asignar' && (
                          <Button
                            onClick={() => handleOpenAsignar(tarea)}
                            className="bg-primary hover:bg-primary/90"
                            data-testid={`asignar-btn-${tarea.id}`}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            Asignar
                          </Button>
                        )}
                        {tipo === 'revisar' && (
                          <Button
                            onClick={() => handleOpenRevisar(tarea)}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid={`revisar-btn-${tarea.id}`}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Revisar
                          </Button>
                        )}
                        {/* Botón Credenciales - visible si el grupo tiene cliente */}
                        {grupo.client_id && (
                          <Button
                            variant="outline"
                            size="icon"
                            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200"
                            onClick={() => {
                              // Usar datos del grupo que contiene las credenciales jerárquicas
                              setTareaCredencialesEquipo({
                                client_name: grupo.cliente,
                                cliente_nombre: grupo.cliente,
                                client_id: grupo.client_id,
                                client_rut: grupo.client_rut,
                                client_clave_sii: grupo.client_clave_sii,
                                client_clave_unica: grupo.client_clave_unica,
                                client_empresas: grupo.client_empresas || []
                              });
                              setShowCredencialesEquipo(true);
                            }}
                            title="Ver credenciales del cliente"
                            data-testid={`credenciales-tarea-${tarea.id}`}
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Botón Eliminar - visible para Líderes/Coordinadores/Socios */}
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          onClick={() => {
                            setTareaAEliminar(tarea);
                            setShowEliminar(true);
                          }}
                          data-testid={`eliminar-tarea-${tarea.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Obtener áreas disponibles para el filtro
  const getAreasDisponibles = () => {
    if (currentUser?.role === 'Administrador') {
      return ['Tributaria', 'Legal', 'Contabilidad', 'Comercial', 'Adm. y Finanzas', 'Socios'];
    }
    return currentUser?.areas || [];
  };

  return (
    <div className='min-h-screen bg-slate-50 flex'>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className='flex-1 p-8 overflow-y-auto max-h-screen'>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className='mb-8'>
            <div className='flex items-center gap-3 mb-2'>
              <ClipboardList className='w-10 h-10 text-primary' strokeWidth={1.5} />
              <h1 className='text-4xl font-bold text-slate-900' data-testid="tablero-lideres-title">
                Tablero de Líderes
              </h1>
            </div>
            <p className='text-lg text-slate-600'>
              Asigna tareas, revisa el trabajo de tu equipo y realiza seguimiento
            </p>
          </div>

          {/* Filtro y Stats */}
          <div className='flex items-center justify-between mb-6'>
            <div className='flex items-center gap-4'>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <Select 
                  value={areaFilter === 'auto' ? getEffectiveAreaFilter() : areaFilter} 
                  onValueChange={setAreaFilter}
                  disabled={currentUser?.role === 'Líder' && currentUser?.areas?.length === 1}
                >
                  <SelectTrigger className='w-[200px]' data-testid="area-filter">
                    <SelectValue placeholder='Filtrar por área' />
                  </SelectTrigger>
                  <SelectContent>
                    {currentUser?.role === 'Administrador' && (
                      <SelectItem value='all'>Todas las áreas</SelectItem>
                    )}
                    {getAreasDisponibles().map(area => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {currentUser?.role === 'Líder' && (
                <span className="text-sm text-slate-500 italic">
                  (Solo puedes ver tareas de tu área)
                </span>
              )}
            </div>

            <div className='flex items-center gap-4'>
              {/* Botón Crear Tarea - Solo para Líder, Coordinador, Administrador */}
              <Button 
                onClick={() => setShowCrearTarea(true)}
                className="bg-primary hover:bg-primary/90"
                data-testid="crear-tarea-lider-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Tarea
              </Button>

              {/* Botón Crear Tarea Periódica */}
              <Button 
                onClick={() => {
                  setPeriodicaForm({
                    titulo: '',
                    descripcion: '',
                    frecuencia: 'mensual',
                    dia_semana: 1,
                    dia_mes: 1,
                    fecha_inicio: '',
                    repeticiones: 12,
                    client_id: '',
                    psp_id: '',
                    attachment_ids: [],
                    sin_cliente: false,
                    asignado_id: '' // Sin asignar inicialmente
                  });
                  setPspsCliente([]);
                  fetchClientesLista();
                  // Cargar funcionarios del área para el selector
                  const effectiveArea = getEffectiveAreaFilter();
                  if (effectiveArea !== 'all') {
                    fetchFuncionariosArea(effectiveArea);
                  }
                  setShowCrearPeriodica(true);
                }}
                variant="outline"
                className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                data-testid="crear-periodica-lider-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Crear Tarea Periódica
              </Button>

              <div className='flex gap-4'>
                <Card className='px-4 py-2 border-amber-200 bg-amber-50'>
                  <div className='flex items-center gap-2'>
                  <Clock className='w-5 h-5 text-amber-600' />
                  <span className='font-semibold text-amber-700'>{totalPorAsignar}</span>
                  <span className='text-sm text-amber-600'>Por Asignar</span>
                </div>
              </Card>
              <Card className='px-4 py-2 border-green-200 bg-green-50'>
                <div className='flex items-center gap-2'>
                  <CheckCircle className='w-5 h-5 text-green-600' />
                  <span className='font-semibold text-green-700'>{tareasPorRevisar.length}</span>
                  <span className='text-sm text-green-600'>Por Revisar</span>
                </div>
              </Card>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="asignar" className="flex items-center gap-2" data-testid="tab-asignar">
                <UserPlus className="w-4 h-4" />
                Tareas por Asignar ({totalPorAsignar})
              </TabsTrigger>
              <TabsTrigger value="revisar" className="flex items-center gap-2" data-testid="tab-revisar">
                <MessageSquare className="w-4 h-4" />
                Completadas por Revisar ({tareasPorRevisar.length})
              </TabsTrigger>
              <TabsTrigger value="seguimiento" className="flex items-center gap-2" data-testid="tab-seguimiento">
                <Users className="w-4 h-4" />
                Seguimiento de Tareas
              </TabsTrigger>
            </TabsList>

            {/* Tab: Tareas por Asignar */}
            <TabsContent value="asignar">
              {loading ? (
                <Card className='p-12 text-center border-slate-200'>
                  <div className='animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4' />
                  <p className='text-slate-600'>Cargando tareas...</p>
                </Card>
              ) : gruposPorAsignar.length === 0 && tareasPeriodicasSinAsignar.length === 0 ? (
                <Card className='p-12 text-center border-slate-200'>
                  <CheckCircle className='w-16 h-16 text-green-500 mx-auto mb-4' strokeWidth={1.5} />
                  <p className='text-lg text-slate-600'>No hay tareas pendientes de asignar</p>
                </Card>
              ) : (
                <div className='space-y-4'>
                  {/* Tareas Periódicas Sin Asignar */}
                  {tareasPeriodicasSinAsignar.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <RefreshCw className="w-5 h-5 text-indigo-600" />
                        <h3 className="font-semibold text-indigo-700">Tareas Periódicas Sin Asignar ({tareasPeriodicasSinAsignar.length})</h3>
                      </div>
                      <div className="space-y-2">
                        {tareasPeriodicasSinAsignar.map(tarea => (
                          <Card key={tarea.id} className="p-4 border-indigo-200 bg-indigo-50/50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full flex items-center gap-1">
                                    <RefreshCw className="w-3 h-3" />
                                    {tarea.frecuencia === 'diaria' ? 'Diaria' : tarea.frecuencia === 'semanal' ? 'Semanal' : 'Mensual'}
                                  </span>
                                  {tarea.client_name && (
                                    <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">{tarea.client_name}</span>
                                  )}
                                  {tarea.psp_folio && (
                                    <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">PSP: {tarea.psp_folio}</span>
                                  )}
                                </div>
                                <h4 className="font-medium text-slate-800">{tarea.titulo}</h4>
                                {tarea.descripcion && (
                                  <p className="text-sm text-slate-600 mt-1">{tarea.descripcion}</p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                  <span>Inicio: {new Date(tarea.fecha_inicio).toLocaleDateString('es-CL')}</span>
                                  <span>Repeticiones: {tarea.repeticiones_total}</span>
                                  <span>Creada por: {tarea.creador_name}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Select
                                  value=""
                                  onValueChange={async (userId) => {
                                    try {
                                      await api.put(`/tareas-periodicas/${tarea.id}/asignar?asignado_id=${userId}`);
                                      toast.success('Tarea periódica asignada exitosamente');
                                      fetchData();
                                    } catch (error) {
                                      toast.error(formatErrorMessage(error));
                                    }
                                  }}
                                >
                                  <SelectTrigger className="w-48">
                                    <SelectValue placeholder="Asignar a..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {funcionariosArea.map(f => (
                                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Tareas Regulares Por Asignar */}
                  {gruposPorAsignar.length > 0 && (
                    <div>
                      {tareasPeriodicasSinAsignar.length > 0 && (
                        <div className="flex items-center gap-2 mb-3">
                          <ClipboardList className="w-5 h-5 text-amber-600" />
                          <h3 className="font-semibold text-amber-700">Tareas Regulares Por Asignar</h3>
                        </div>
                      )}
                      <div className='space-y-2'>
                        {gruposPorAsignar.map((grupo, idx) => renderGrupoJerarquico(grupo, idx, 'asignar'))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Tab: Tareas Completadas por Revisar */}
            <TabsContent value="revisar">
              {gruposPorRevisar.length === 0 ? (
                <Card className='p-12 text-center border-slate-200'>
                  <CheckCircle className='w-16 h-16 text-green-500 mx-auto mb-4' strokeWidth={1.5} />
                  <p className='text-lg text-slate-600'>No hay tareas pendientes de revisión</p>
                </Card>
              ) : (
                <div className='space-y-2'>
                  {gruposPorRevisar.map((grupo, idx) => renderGrupoJerarquico(grupo, idx, 'revisar'))}
                </div>
              )}
            </TabsContent>

            {/* Tab: Seguimiento de Tareas */}
            <TabsContent value="seguimiento">
              <div className="space-y-6">
                {/* Barra superior: Filtros de área y Toggle de vista */}
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  {/* Filtro: Mi área vs Otras áreas */}
                  <div className="flex bg-slate-100 rounded-lg p-1">
                    <button
                      onClick={() => {
                        setFiltroAreaSeguimiento('mi_area');
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        filtroAreaSeguimiento === 'mi_area'
                          ? 'bg-white text-primary shadow-sm'
                          : 'text-slate-600 hover:text-slate-800'
                      }`}
                      data-testid="filtro-mi-area"
                    >
                      Mi Área
                    </button>
                    <button
                      onClick={() => {
                        setFiltroAreaSeguimiento('otras_areas');
                        fetchTareasOtrasAreas();
                      }}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        filtroAreaSeguimiento === 'otras_areas'
                          ? 'bg-white text-primary shadow-sm'
                          : 'text-slate-600 hover:text-slate-800'
                      }`}
                      data-testid="filtro-otras-areas"
                    >
                      Otras Áreas
                    </button>
                  </div>
                  
                  {/* Toggle de Vista: Funcionario vs Cliente */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Vista por:</span>
                    <div className="flex bg-slate-100 rounded-lg p-1">
                      <button
                        onClick={() => {
                          setVistaMode('funcionario');
                          setSelectedCliente(null);
                        }}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                          vistaMode === 'funcionario'
                            ? 'bg-white text-primary shadow-sm'
                            : 'text-slate-600 hover:text-slate-800'
                        }`}
                        data-testid="vista-funcionario"
                      >
                        <User className="w-4 h-4" />
                        Funcionario
                      </button>
                      <button
                        onClick={() => {
                          setVistaMode('cliente');
                          setSelectedFuncionario(null);
                          fetchClientesConTareas();
                        }}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                          vistaMode === 'cliente'
                            ? 'bg-white text-primary shadow-sm'
                            : 'text-slate-600 hover:text-slate-800'
                        }`}
                        data-testid="vista-cliente"
                      >
                        <Briefcase className="w-4 h-4" />
                        Cliente
                      </button>
                    </div>
                  </div>
                  
                  {/* Filtro de fechas */}
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-600 whitespace-nowrap">Desde:</Label>
                    <Input
                      type="date"
                      value={fechaInicio}
                      onChange={(e) => {
                        setFechaInicio(e.target.value);
                        if (vistaMode === 'funcionario' && selectedFuncionario) {
                          fetchTareasEquipo(selectedFuncionario.id, selectedFuncionario.name);
                        } else if (vistaMode === 'cliente' && selectedCliente) {
                          fetchTareasCliente(selectedCliente.id, selectedCliente.name);
                        }
                      }}
                      className="w-36"
                      data-testid="filtro-fecha-inicio"
                    />
                    <Label className="text-sm text-slate-600 whitespace-nowrap">Hasta:</Label>
                    <Input
                      type="date"
                      value={fechaFin}
                      onChange={(e) => {
                        setFechaFin(e.target.value);
                        if (vistaMode === 'funcionario' && selectedFuncionario) {
                          fetchTareasEquipo(selectedFuncionario.id, selectedFuncionario.name);
                        } else if (vistaMode === 'cliente' && selectedCliente) {
                          fetchTareasCliente(selectedCliente.id, selectedCliente.name);
                        }
                      }}
                      className="w-36"
                      data-testid="filtro-fecha-fin"
                    />
                  </div>
                </div>
                
                {/* Selector dinámico de Cliente (solo en vista funcionario) */}
                {vistaMode === 'funcionario' && (
                  <div className="flex gap-3 items-center">
                    <Label className="text-sm text-slate-600 whitespace-nowrap">Filtrar por cliente:</Label>
                    <select
                      value={filtroClienteSelect}
                      onChange={(e) => setFiltroClienteSelect(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white min-w-[200px]"
                    >
                      <option value="">Todos los clientes</option>
                      {clientesConTareas.map(cliente => (
                        <option key={cliente.id} value={cliente.id}>
                          {cliente.name} ({cliente.tareas.pendientes + cliente.tareas.vencidas + cliente.tareas.enRevision} activas)
                        </option>
                      ))}
                    </select>
                    {filtroClienteSelect && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFiltroClienteSelect('')}
                        className="text-slate-500"
                      >
                        Limpiar filtro
                      </Button>
                    )}
                  </div>
                )}

                {filtroAreaSeguimiento === 'mi_area' ? (
                  /* ============ MI ÁREA ============ */
                  <div className="grid grid-cols-12 gap-6">
                    {/* Panel izquierdo: Selector de funcionario o cliente */}
                    <div className="col-span-4">
                      {vistaMode === 'funcionario' ? (
                        /* Lista de Funcionarios */
                        <Card className="p-4 border-slate-200">
                          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                            <Users className="w-5 h-5 text-primary" />
                            Funcionarios del Área
                          </h3>
                          
                          {funcionariosArea.length === 0 ? (
                            <p className="text-sm text-slate-500">
                              No hay funcionarios en esta área
                            </p>
                          ) : (
                            <div className="space-y-2 max-h-[600px] overflow-y-auto">
                              {funcionariosArea.map(func => (
                                <button
                                  key={func.id}
                                  onClick={() => handleSelectFuncionario(func)}
                                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                                    selectedFuncionario?.id === func.id
                                      ? 'border-primary bg-primary/5'
                                      : 'border-slate-200 hover:bg-slate-50'
                                  }`}
                                data-testid={`funcionario-${func.id}`}
                              >
                                <p className="font-medium text-slate-800">{func.name}</p>
                                <p className="text-xs text-slate-500">{func.cargo || func.role}</p>
                              </button>
                            ))}
                          </div>
                        )}
                      </Card>
                      ) : (
                        /* Lista de Clientes */
                        <Card className="p-4 border-slate-200">
                          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-primary" />
                            Clientes con Tareas
                          </h3>
                          
                          {loadingClientes ? (
                            <p className="text-sm text-slate-500">Cargando clientes...</p>
                          ) : clientesConTareas.length === 0 ? (
                            <p className="text-sm text-slate-500">
                              No hay clientes con tareas activas
                            </p>
                          ) : (
                            <div className="space-y-2 max-h-[600px] overflow-y-auto">
                              {clientesConTareas.map(cliente => (
                                <button
                                  key={cliente.id}
                                  onClick={() => handleSelectCliente(cliente)}
                                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                                    selectedCliente?.id === cliente.id
                                      ? 'border-primary bg-primary/5'
                                      : 'border-slate-200 hover:bg-slate-50'
                                  }`}
                                  data-testid={`cliente-${cliente.id}`}
                                >
                                  <p className="font-medium text-slate-800">{cliente.name}</p>
                                  <div className="flex gap-2 mt-1">
                                    {cliente.tareas.pendientes > 0 && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                        {cliente.tareas.pendientes} pendientes
                                      </span>
                                    )}
                                    {cliente.tareas.vencidas > 0 && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                                        {cliente.tareas.vencidas} vencidas
                                      </span>
                                    )}
                                    {cliente.tareas.enRevision > 0 && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                        {cliente.tareas.enRevision} en revisión
                                      </span>
                                    )}
                                    {cliente.tareas.realizadas > 0 && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                                        {cliente.tareas.realizadas} realizadas
                                      </span>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </Card>
                      )}
                    </div>

                    {/* Panel derecho: Tareas del funcionario o cliente */}
                    <div className="col-span-8">
                      {vistaMode === 'funcionario' ? (
                        /* Vista por Funcionario */
                        <>
                          {!selectedFuncionario ? (
                        <Card className="p-12 text-center border-slate-200">
                          <User className="w-16 h-16 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
                          <p className="text-lg text-slate-600">
                            Selecciona un funcionario para ver sus tareas
                          </p>
                        </Card>
                      ) : (
                        <div className="space-y-4">
                          {/* Encabezado del funcionario */}
                          <Card className="p-4 border-slate-200 bg-primary/5">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="font-semibold text-slate-800">{selectedFuncionario.name}</h3>
                                <p className="text-sm text-slate-500">{selectedFuncionario.cargo || selectedFuncionario.role}</p>
                              </div>
                            </div>
                          </Card>

                          {/* Tareas Pendientes */}
                          <Card className="p-4 border-slate-200">
                            <div className="flex items-center gap-2 mb-3">
                              <Clock className="w-5 h-5 text-amber-500" />
                              <h4 className="font-semibold text-slate-800">Tareas Pendientes</h4>
                              <Badge variant="outline" className="ml-auto">
                                {filtrarTareasEquipo(tareasEquipo.pendientes).filter(t => new Date(t.deadline) >= new Date()).length}
                              </Badge>
                            </div>
                            {filtrarTareasEquipo(tareasEquipo.pendientes).filter(t => new Date(t.deadline) >= new Date()).length === 0 ? (
                              <p className="text-sm text-slate-500">No hay tareas pendientes</p>
                            ) : (
                              <div className="space-y-2">
                                {filtrarTareasEquipo(tareasEquipo.pendientes).filter(t => new Date(t.deadline) >= new Date()).map(tarea => (
                                  <div key={tarea.id} className="p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <p className="font-medium text-slate-800">{tarea.title}</p>
                                          {tarea.es_periodica && (
                                            <Badge variant="outline" className="bg-purple-100 text-purple-700 text-xs border-purple-300">
                                              <RefreshCw className="w-3 h-3 mr-1" />
                                              Periódica
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-xs text-slate-500">
                                          {tarea.client_name} • {tarea.psp_folio}
                                        </p>
                                        <Badge variant="outline" className="mt-1 text-xs">{tarea.status}</Badge>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-xs text-slate-500">
                                          Vence: {new Date(tarea.deadline).toLocaleDateString('es-CL')}
                                        </p>
                                        {/* Botones de acción */}
                                        <div className="flex gap-1 mt-2 justify-end">
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => handleOpenEditarEquipo(tarea)}
                                            title="Editar tarea"
                                          >
                                            <Pencil className="w-3 h-3" />
                                          </Button>
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => { setTareaReasignarEquipo(tarea); fetchUsuariosAsignables(tarea.area); setShowReasignarEquipo(true); }}
                                            title="Reasignar tarea"
                                          >
                                            <UserPlus className="w-3 h-3" />
                                          </Button>
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => { setTareaBitacoraEquipo(tarea); setShowBitacoraEquipo(true); }}
                                            title="Ver bitácora"
                                          >
                                            <FileText className="w-3 h-3" />
                                          </Button>
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => { handleOpenCredencialesEquipo(tarea); }}
                                            title="Ver credenciales"
                                          >
                                            <Key className="w-3 h-3" />
                                          </Button>
                                          {puedoEliminarTarea(tarea) && (
                                            <Button 
                                              variant="ghost" 
                                              size="sm" 
                                              className="h-7 px-2 text-red-500 hover:text-red-700"
                                              onClick={() => { setTareaAEliminar(tarea); setShowEliminar(true); }}
                                              title="Eliminar tarea"
                                            >
                                              <Trash2 className="w-3 h-3" />
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </Card>

                          {/* Tareas Vencidas */}
                          {filtrarTareasEquipo(tareasEquipo.pendientes).filter(t => new Date(t.deadline) < new Date()).length > 0 && (
                            <Card className="p-4 border-red-200 bg-red-50/50">
                              <div className="flex items-center gap-2 mb-3">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                                <h4 className="font-semibold text-red-800">Tareas Vencidas</h4>
                                <Badge variant="destructive" className="ml-auto">
                                  {filtrarTareasEquipo(tareasEquipo.pendientes).filter(t => new Date(t.deadline) < new Date()).length}
                                </Badge>
                              </div>
                              <div className="space-y-2">
                                {filtrarTareasEquipo(tareasEquipo.pendientes).filter(t => new Date(t.deadline) < new Date()).map(tarea => (
                                  <div key={tarea.id} className="p-3 rounded-lg border border-red-300 bg-white">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <p className="font-medium text-slate-800">{tarea.title}</p>
                                          {tarea.es_periodica && (
                                            <Badge variant="outline" className="bg-purple-100 text-purple-700 text-xs border-purple-300">
                                              <RefreshCw className="w-3 h-3 mr-1" />
                                              Periódica
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-xs text-slate-500">
                                          {tarea.client_name} • {tarea.psp_folio}
                                        </p>
                                        <Badge variant="outline" className="mt-1 text-xs">{tarea.status}</Badge>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-xs text-red-600 font-medium">
                                          Venció: {new Date(tarea.deadline).toLocaleDateString('es-CL')}
                                        </p>
                                        {/* Botones de acción */}
                                        <div className="flex gap-1 mt-2 justify-end">
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => handleOpenEditarEquipo(tarea)}
                                            title="Editar tarea"
                                          >
                                            <Pencil className="w-3 h-3" />
                                          </Button>
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => { setTareaReasignarEquipo(tarea); fetchUsuariosAsignables(tarea.area); setShowReasignarEquipo(true); }}
                                            title="Reasignar tarea"
                                          >
                                            <UserPlus className="w-3 h-3" />
                                          </Button>
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => { setTareaBitacoraEquipo(tarea); setShowBitacoraEquipo(true); }}
                                            title="Ver bitácora"
                                          >
                                            <FileText className="w-3 h-3" />
                                          </Button>
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => { handleOpenCredencialesEquipo(tarea); }}
                                            title="Ver credenciales"
                                          >
                                            <Key className="w-3 h-3" />
                                          </Button>
                                          {puedoEliminarTarea(tarea) && (
                                            <Button 
                                              variant="ghost" 
                                              size="sm" 
                                              className="h-7 px-2 text-red-500 hover:text-red-700"
                                              onClick={() => { setTareaAEliminar(tarea); setShowEliminar(true); }}
                                              title="Eliminar tarea"
                                            >
                                              <Trash2 className="w-3 h-3" />
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}

                          {/* Tareas en Revisión */}
                          {filtrarTareasEquipo(tareasEquipo.enRevision || []).length > 0 && (
                            <Card className="p-4 border-blue-200 bg-blue-50/50">
                              <div className="flex items-center gap-2 mb-3">
                                <MessageSquare className="w-5 h-5 text-blue-500" />
                                <h4 className="font-semibold text-blue-800">Tareas en Revisión</h4>
                                <Badge variant="outline" className="ml-auto bg-blue-100 text-blue-700">
                                  {filtrarTareasEquipo(tareasEquipo.enRevision || []).length}
                                </Badge>
                              </div>
                              <div className="space-y-2">
                                {filtrarTareasEquipo(tareasEquipo.enRevision || []).map(tarea => (
                                  <div key={tarea.id} className="p-3 rounded-lg border border-blue-300 bg-white">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <p className="font-medium text-slate-800">{tarea.title}</p>
                                          {tarea.es_periodica && (
                                            <Badge variant="outline" className="bg-purple-100 text-purple-700 text-xs border-purple-300">
                                              <RefreshCw className="w-3 h-3 mr-1" />
                                              Periódica
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-xs text-slate-500">
                                          {tarea.client_name} • {tarea.psp_folio}
                                        </p>
                                        <Badge variant="outline" className="mt-1 text-xs bg-blue-100 text-blue-700">{tarea.status}</Badge>
                                      </div>
                                      <div className="text-right flex-shrink-0">
                                        <p className="text-xs text-blue-600 font-medium">Esperando revisión</p>
                                        {tarea.fecha_completada && (
                                          <p className="text-xs text-slate-500">
                                            En revisión desde: {new Date(tarea.fecha_completada).toLocaleDateString('es-CL')}
                                          </p>
                                        )}
                                        {tarea.deadline && (
                                          <p className="text-xs text-slate-500">
                                            Plazo original: {new Date(tarea.deadline).toLocaleDateString('es-CL')}
                                          </p>
                                        )}
                                        {/* Botones de acción */}
                                        <div className="flex gap-1 mt-2 justify-end">
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => { setTareaBitacoraEquipo(tarea); setShowBitacoraEquipo(true); }}
                                            title="Ver bitácora"
                                          >
                                            <FileText className="w-3 h-3" />
                                          </Button>
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => { handleOpenCredencialesEquipo(tarea); }}
                                            title="Ver credenciales"
                                          >
                                            <Key className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}

                          {/* Tareas Realizadas */}
                          <Card className="p-4 border-slate-200">
                            <div className="flex items-center gap-2 mb-3">
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <h4 className="font-semibold text-slate-800">Tareas Realizadas</h4>
                              <Badge variant="outline" className="ml-auto bg-green-50 text-green-700">
                                {filtrarTareasEquipo(tareasEquipo.realizadas).length}
                              </Badge>
                            </div>
                            {filtrarTareasEquipo(tareasEquipo.realizadas).length === 0 ? (
                              <p className="text-sm text-slate-500">No hay tareas realizadas</p>
                            ) : (
                              <div className="space-y-2">
                                {filtrarTareasEquipo(tareasEquipo.realizadas).slice(0, 10).map(tarea => {
                                  // Obtener fechas relevantes de las gestiones
                                  const gestiones = tarea.gestiones || [];
                                  const fechaEnvioRevision = gestiones.find(g => g.tipo === 'completada' || g.tipo === 'envio_revision')?.fecha;
                                  const fechaDevolucion = gestiones.filter(g => g.tipo === 'devolucion' || g.tipo === 'devuelta').pop()?.fecha;
                                  const fechaAprobacion = gestiones.find(g => g.tipo === 'aprobacion' || g.tipo === 'aprobada')?.fecha || tarea.fecha_aprobacion;
                                  
                                  return (
                                  <div key={tarea.id} className="p-3 rounded-lg border border-green-200 bg-green-50/50">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <p className="font-medium text-slate-800">{tarea.title}</p>
                                        <p className="text-xs text-slate-500">
                                          {tarea.client_name} • {tarea.psp_folio}
                                        </p>
                                        {/* Información de fechas */}
                                        <div className="mt-2 space-y-0.5 text-xs text-slate-500">
                                          {tarea.deadline && (
                                            <p>Plazo original: {new Date(tarea.deadline).toLocaleDateString('es-CL')}</p>
                                          )}
                                          {(fechaEnvioRevision || tarea.fecha_completada) && (
                                            <p>Enviada a revisión: {new Date(fechaEnvioRevision || tarea.fecha_completada).toLocaleDateString('es-CL')}</p>
                                          )}
                                          {fechaDevolucion && (
                                            <p className="text-amber-600">Devuelta: {new Date(fechaDevolucion).toLocaleDateString('es-CL')}</p>
                                          )}
                                          {(fechaAprobacion || tarea.updated_at) && (() => {
                                            const fechaCumplimiento = new Date(fechaAprobacion || tarea.updated_at);
                                            const fechaPlazo = tarea.deadline ? new Date(tarea.deadline) : null;
                                            const dentroDePlazo = !fechaPlazo || fechaCumplimiento <= fechaPlazo;
                                            
                                            return (
                                              <p className={dentroDePlazo ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                                                Cumplida: {fechaCumplimiento.toLocaleDateString('es-CL')}
                                                {fechaPlazo && (
                                                  <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${dentroDePlazo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                    {dentroDePlazo ? '✓ Dentro de plazo' : '⚠ Fuera de plazo'}
                                                  </span>
                                                )}
                                              </p>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                      <div className="text-right flex-shrink-0">
                                        <div className="flex gap-1 items-center justify-end">
                                          {tarea.es_periodica && (
                                            <Badge variant="outline" className="bg-purple-100 text-purple-700 text-xs border-purple-300">
                                              <RefreshCw className="w-3 h-3 mr-1" />
                                              Periódica
                                            </Badge>
                                          )}
                                          <Badge variant="outline" className="bg-green-100 text-green-700 text-xs">
                                            {tarea.status}
                                          </Badge>
                                        </div>
                                        {/* Botones de acción */}
                                        <div className="flex gap-1 mt-2 justify-end">
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => { setTareaBitacoraEquipo(tarea); setShowBitacoraEquipo(true); }}
                                            title="Ver bitácora"
                                          >
                                            <FileText className="w-3 h-3" />
                                          </Button>
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-7 px-2"
                                            onClick={() => { handleOpenCredencialesEquipo(tarea); }}
                                            title="Ver credenciales"
                                          >
                                            <Key className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </Card>
                        </div>
                      )}
                        </>
                      ) : (
                        /* Vista por Cliente */
                        <>
                          {!selectedCliente ? (
                            <Card className="p-12 text-center border-slate-200">
                              <Briefcase className="w-16 h-16 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
                              <p className="text-lg text-slate-600">
                                Selecciona un cliente para ver sus tareas
                              </p>
                            </Card>
                          ) : (
                            <div className="space-y-4">
                              {/* Encabezado del cliente */}
                              <Card className="p-4 border-slate-200 bg-primary/5">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h3 className="font-semibold text-slate-800">{selectedCliente.name}</h3>
                                    <p className="text-sm text-slate-500">
                                      {tareasCliente.pendientes.length + tareasCliente.vencidas.length + tareasCliente.enRevision.length} tareas activas • {tareasCliente.realizadas.length} realizadas
                                    </p>
                                  </div>
                                </div>
                              </Card>

                              {/* Tareas Vencidas del Cliente */}
                              {tareasCliente.vencidas.length > 0 && (
                                <Card className="p-4 border-red-200 bg-red-50/50">
                                  <div className="flex items-center gap-2 mb-3">
                                    <AlertTriangle className="w-5 h-5 text-red-500" />
                                    <h4 className="font-semibold text-red-800">Tareas Vencidas</h4>
                                    <Badge variant="outline" className="ml-auto bg-red-100 text-red-700 border-red-300">
                                      {tareasCliente.vencidas.length}
                                    </Badge>
                                  </div>
                                  <div className="space-y-2">
                                    {tareasCliente.vencidas.map(tarea => (
                                      <div key={tarea.id} className="p-3 rounded-lg border border-red-200 bg-white">
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <p className="font-medium text-slate-800">{tarea.title}</p>
                                            <p className="text-xs text-primary font-medium">
                                              Responsable: {tarea.responsible}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                              PSP: {tarea.psp_folio}
                                            </p>
                                          </div>
                                          <div className="text-right">
                                            <Badge variant="outline" className="text-xs bg-red-100 text-red-700">{tarea.status}</Badge>
                                            <p className="text-xs text-red-600 mt-1">
                                              Venció: {new Date(tarea.deadline).toLocaleDateString('es-CL')}
                                            </p>
                                            {/* Botones de acción */}
                                            <div className="flex gap-1 mt-2 justify-end">
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => handleOpenEditarEquipo(tarea)} title="Editar tarea">
                                                <Pencil className="w-3 h-3" />
                                              </Button>
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setTareaReasignarEquipo(tarea); fetchUsuariosAsignables(tarea.area); setShowReasignarEquipo(true); }} title="Reasignar">
                                                <UserPlus className="w-3 h-3" />
                                              </Button>
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setTareaBitacoraEquipo(tarea); setShowBitacoraEquipo(true); }} title="Ver bitácora">
                                                <FileText className="w-3 h-3" />
                                              </Button>
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { handleOpenCredencialesEquipo(tarea); }} title="Ver credenciales">
                                                <Key className="w-3 h-3" />
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </Card>
                              )}

                              {/* Tareas Pendientes del Cliente */}
                              <Card className="p-4 border-slate-200">
                                <div className="flex items-center gap-2 mb-3">
                                  <Clock className="w-5 h-5 text-amber-500" />
                                  <h4 className="font-semibold text-slate-800">Tareas Pendientes</h4>
                                  <Badge variant="outline" className="ml-auto">
                                    {tareasCliente.pendientes.length}
                                  </Badge>
                                </div>
                                {tareasCliente.pendientes.length === 0 ? (
                                  <p className="text-sm text-slate-500">No hay tareas pendientes</p>
                                ) : (
                                  <div className="space-y-2">
                                    {tareasCliente.pendientes.map(tarea => (
                                      <div key={tarea.id} className="p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <p className="font-medium text-slate-800">{tarea.title}</p>
                                            <p className="text-xs text-primary font-medium">
                                              Responsable: {tarea.responsible}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                              PSP: {tarea.psp_folio}
                                            </p>
                                          </div>
                                          <div className="text-right">
                                            <Badge variant="outline" className="text-xs">{tarea.status}</Badge>
                                            <p className="text-xs text-slate-500 mt-1">
                                              Vence: {tarea.deadline ? new Date(tarea.deadline).toLocaleDateString('es-CL') : 'Sin fecha'}
                                            </p>
                                            {/* Botones de acción */}
                                            <div className="flex gap-1 mt-2 justify-end">
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => handleOpenEditarEquipo(tarea)} title="Editar tarea">
                                                <Pencil className="w-3 h-3" />
                                              </Button>
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setTareaReasignarEquipo(tarea); fetchUsuariosAsignables(tarea.area); setShowReasignarEquipo(true); }} title="Reasignar">
                                                <UserPlus className="w-3 h-3" />
                                              </Button>
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setTareaBitacoraEquipo(tarea); setShowBitacoraEquipo(true); }} title="Ver bitácora">
                                                <FileText className="w-3 h-3" />
                                              </Button>
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { handleOpenCredencialesEquipo(tarea); }} title="Ver credenciales">
                                                <Key className="w-3 h-3" />
                                              </Button>
                                              {puedoEliminarTarea(tarea) && (
                                                <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:text-red-700" onClick={() => { setTareaAEliminar(tarea); setShowEliminar(true); }} title="Eliminar tarea">
                                                  <Trash2 className="w-3 h-3" />
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </Card>

                              {/* Tareas en Revisión del Cliente */}
                              <Card className="p-4 border-blue-200 bg-blue-50/30">
                                <div className="flex items-center gap-2 mb-3">
                                  <Clock className="w-5 h-5 text-blue-500" />
                                  <h4 className="font-semibold text-slate-800">Tareas en Revisión</h4>
                                  <Badge variant="outline" className="ml-auto bg-blue-100 text-blue-700">
                                    {tareasCliente.enRevision.length}
                                  </Badge>
                                </div>
                                {tareasCliente.enRevision.length === 0 ? (
                                  <p className="text-sm text-slate-500">No hay tareas en revisión</p>
                                ) : (
                                  <div className="space-y-2">
                                    {tareasCliente.enRevision.map(tarea => (
                                      <div key={tarea.id} className="p-3 rounded-lg border border-blue-200 bg-white">
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <p className="font-medium text-slate-800">{tarea.title}</p>
                                            <p className="text-xs text-primary font-medium">
                                              Responsable: {tarea.responsible}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                              PSP: {tarea.psp_folio}
                                            </p>
                                          </div>
                                          <div className="text-right">
                                            <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700">{tarea.status}</Badge>
                                            {tarea.fecha_completada && (
                                              <p className="text-xs text-slate-500 mt-1">
                                                En revisión desde: {new Date(tarea.fecha_completada).toLocaleDateString('es-CL')}
                                              </p>
                                            )}
                                            {/* Botones de acción */}
                                            <div className="flex gap-1 mt-2 justify-end">
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setTareaBitacoraEquipo(tarea); setShowBitacoraEquipo(true); }} title="Ver bitácora">
                                                <FileText className="w-3 h-3" />
                                              </Button>
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { handleOpenCredencialesEquipo(tarea); }} title="Ver credenciales">
                                                <Key className="w-3 h-3" />
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </Card>

                              {/* Tareas Realizadas del Cliente */}
                              <Card className="p-4 border-green-200 bg-green-50/30">
                                <div className="flex items-center gap-2 mb-3">
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                  <h4 className="font-semibold text-slate-800">Tareas Realizadas</h4>
                                  <Badge variant="outline" className="ml-auto bg-green-100 text-green-700">
                                    {tareasCliente.realizadas.length}
                                  </Badge>
                                </div>
                                {tareasCliente.realizadas.length === 0 ? (
                                  <p className="text-sm text-slate-500">No hay tareas realizadas</p>
                                ) : (
                                  <div className="space-y-2">
                                    {tareasCliente.realizadas.map(tarea => (
                                      <div key={tarea.id} className="p-3 rounded-lg border border-green-200 bg-white">
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <p className="font-medium text-slate-800">{tarea.title}</p>
                                            <p className="text-xs text-primary font-medium">
                                              Responsable: {tarea.responsible}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                              PSP: {tarea.psp_folio}
                                            </p>
                                          </div>
                                          <div className="text-right">
                                            <Badge variant="outline" className="text-xs bg-green-100 text-green-700">{tarea.status}</Badge>
                                            {/* Botones de acción */}
                                            <div className="flex gap-1 mt-2 justify-end">
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setTareaBitacoraEquipo(tarea); setShowBitacoraEquipo(true); }} title="Ver bitácora">
                                                <FileText className="w-3 h-3" />
                                              </Button>
                                              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { handleOpenCredencialesEquipo(tarea); }} title="Ver credenciales">
                                                <Key className="w-3 h-3" />
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </Card>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  /* ============ OTRAS ÁREAS ============ */
                  <div className="space-y-6">
                    <Card className="p-4 border-slate-200 bg-blue-50/50">
                      <p className="text-sm text-blue-700">
                        <Info className="w-4 h-4 inline mr-2" />
                        Mostrando tareas que <strong>tú creaste</strong> para funcionarios de otras áreas (distintas a la tuya).
                      </p>
                    </Card>

                    {tareasOtrasAreas.length === 0 ? (
                      <Card className="p-12 text-center border-slate-200">
                        <Briefcase className="w-16 h-16 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
                        <p className="text-lg text-slate-600">No has creado tareas para otras áreas</p>
                        <p className="text-sm text-slate-500">Las tareas que asignes a funcionarios de otras áreas aparecerán aquí</p>
                      </Card>
                    ) : (
                      <div className="grid gap-6">
                        {/* Tareas Pendientes de Otras Áreas */}
                        <Card className="p-4 border-slate-200">
                          <div className="flex items-center gap-2 mb-4">
                            <Clock className="w-5 h-5 text-amber-500" />
                            <h4 className="font-semibold text-slate-800">Tareas Pendientes</h4>
                            <Badge variant="outline" className="ml-auto">
                              {filtrarTareasEquipo(tareasOtrasAreas.filter(t => !['Aprobada', 'Cumplida', 'Completada'].includes(t.status) && !t.vencida)).length}
                            </Badge>
                          </div>
                          {filtrarTareasEquipo(tareasOtrasAreas.filter(t => !['Aprobada', 'Cumplida', 'Completada'].includes(t.status) && !t.vencida)).length === 0 ? (
                            <p className="text-sm text-slate-500">No hay tareas pendientes</p>
                          ) : (
                            <div className="space-y-2">
                              {filtrarTareasEquipo(tareasOtrasAreas.filter(t => !['Aprobada', 'Cumplida', 'Completada'].includes(t.status) && !t.vencida)).map(tarea => (
                                <div key={tarea.id} className="p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <p className="font-medium text-slate-800">{tarea.title}</p>
                                      <p className="text-sm text-primary font-medium">
                                        Asignado a: {tarea.responsible}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        Área: {tarea.area} • {tarea.client_name} • {tarea.psp_folio}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <Badge variant="outline" className="text-xs">{tarea.status}</Badge>
                                      <p className="text-xs text-slate-500 mt-1">
                                        Vence: {tarea.deadline ? new Date(tarea.deadline).toLocaleDateString('es-CL') : 'Sin fecha'}
                                      </p>
                                      {/* Botones de acción */}
                                      <div className="flex gap-1 mt-2 justify-end">
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          className="h-7 px-2"
                                          onClick={() => { setTareaBitacoraEquipo(tarea); setShowBitacoraEquipo(true); }}
                                          title="Ver bitácora"
                                        >
                                          <FileText className="w-3 h-3" />
                                        </Button>
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          className="h-7 px-2"
                                          onClick={() => { handleOpenCredencialesEquipo(tarea); }}
                                          title="Ver credenciales"
                                        >
                                          <Key className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </Card>

                        {/* Tareas en Revisión de Otras Áreas */}
                        {filtrarTareasEquipo(tareasOtrasAreas.filter(t => ['En Revisión', 'Revisión Socios'].includes(t.status))).length > 0 && (
                          <Card className="p-4 border-blue-200 bg-blue-50/50">
                            <div className="flex items-center gap-2 mb-4">
                              <MessageSquare className="w-5 h-5 text-blue-500" />
                              <h4 className="font-semibold text-blue-800">Tareas en Revisión</h4>
                              <Badge variant="outline" className="ml-auto bg-blue-100 text-blue-700">
                                {filtrarTareasEquipo(tareasOtrasAreas.filter(t => ['Por Revisar', 'Cumplida'].includes(t.status))).length}
                              </Badge>
                            </div>
                            <div className="space-y-2">
                              {filtrarTareasEquipo(tareasOtrasAreas.filter(t => ['En Revisión', 'Revisión Socios'].includes(t.status))).map(tarea => (
                                <div key={tarea.id} className="p-3 rounded-lg border border-blue-300 bg-white">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <p className="font-medium text-slate-800">{tarea.title}</p>
                                      <p className="text-sm text-blue-600 font-medium">
                                        Realizado por: {tarea.responsible}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        Área: {tarea.area} • {tarea.client_name}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700">{tarea.status}</Badge>
                                      <p className="text-xs text-blue-600 mt-1">Esperando revisión</p>
                                      <div className="flex gap-1 mt-2 justify-end">
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          className="h-7 px-2"
                                          onClick={() => { setTareaBitacoraEquipo(tarea); setShowBitacoraEquipo(true); }}
                                          title="Ver bitácora"
                                        >
                                          <FileText className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Card>
                        )}

                        {/* Tareas Vencidas de Otras Áreas */}
                        {filtrarTareasEquipo(tareasOtrasAreas.filter(t => t.vencida && !['Aprobada', 'Cumplida', 'Completada'].includes(t.status))).length > 0 && (
                          <Card className="p-4 border-red-200 bg-red-50/50">
                            <div className="flex items-center gap-2 mb-4">
                              <AlertTriangle className="w-5 h-5 text-red-500" />
                              <h4 className="font-semibold text-red-800">Tareas Vencidas</h4>
                              <Badge variant="destructive" className="ml-auto">
                                {filtrarTareasEquipo(tareasOtrasAreas.filter(t => t.vencida && !['Aprobada', 'Cumplida', 'Completada'].includes(t.status))).length}
                              </Badge>
                            </div>
                            <div className="space-y-2">
                              {filtrarTareasEquipo(tareasOtrasAreas.filter(t => t.vencida && !['Aprobada', 'Cumplida', 'Completada'].includes(t.status))).map(tarea => (
                                <div key={tarea.id} className="p-3 rounded-lg border border-red-300 bg-white">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <p className="font-medium text-slate-800">{tarea.title}</p>
                                      <p className="text-sm text-red-600 font-medium">
                                        Asignado a: {tarea.responsible}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        Área: {tarea.area} • {tarea.client_name}
                                      </p>
                                    </div>
                                    <p className="text-xs text-red-600 font-medium">
                                      Venció: {tarea.deadline ? new Date(tarea.deadline).toLocaleDateString('es-CL') : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Card>
                        )}

                        {/* Tareas Realizadas de Otras Áreas */}
                        <Card className="p-4 border-slate-200">
                          <div className="flex items-center gap-2 mb-4">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <h4 className="font-semibold text-slate-800">Tareas Realizadas</h4>
                            <Badge variant="outline" className="ml-auto bg-green-50 text-green-700">
                              {filtrarTareasEquipo(tareasOtrasAreas.filter(t => ['Aprobada', 'Cumplida', 'Completada'].includes(t.status))).length}
                            </Badge>
                          </div>
                          {filtrarTareasEquipo(tareasOtrasAreas.filter(t => ['Aprobada', 'Cumplida', 'Completada'].includes(t.status))).length === 0 ? (
                            <p className="text-sm text-slate-500">No hay tareas realizadas</p>
                          ) : (
                            <div className="space-y-2">
                              {filtrarTareasEquipo(tareasOtrasAreas.filter(t => ['Aprobada', 'Cumplida', 'Completada'].includes(t.status))).map(tarea => (
                                <div key={tarea.id} className="p-3 rounded-lg border border-green-200 bg-green-50/50">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <p className="font-medium text-slate-800">{tarea.title}</p>
                                      <p className="text-sm text-green-700 font-medium">
                                        Realizado por: {tarea.responsible}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        Área: {tarea.area} • {tarea.client_name}
                                      </p>
                                    </div>
                                    <Badge variant="outline" className="bg-green-100 text-green-700 text-xs">
                                      {tarea.status}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </Card>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Modal Asignar Tarea */}
      <Dialog open={showAsignar} onOpenChange={(open) => {
        setShowAsignar(open);
        if (!open) {
          setAdjuntosAsignacion([]);
          setPlazoPspInfo(null);
          setPlazoExcedePsp(false);
          setJustificacionPlazo('');
        }
      }}>
        <DialogContent className='max-w-5xl w-[95vw] lg:w-[85vw] max-h-[95vh] overflow-y-auto bg-white'>
          <DialogHeader>
            <DialogTitle className='text-lg font-semibold text-slate-900'>Asignar Tarea</DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Seleccione un funcionario y adjunte documentos base para la ejecución
            </DialogDescription>
          </DialogHeader>
          {selectedTarea && (
            <div className='mt-4'>
              {/* Detalle de la tarea - Compacto */}
              <div className='bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4'>
                <p className='text-sm font-medium text-slate-800 line-clamp-2'>{selectedTarea.title}</p>
                {selectedTarea.description && (
                  <p className='text-xs text-slate-600 mt-1 line-clamp-2'>{selectedTarea.description}</p>
                )}
              </div>

              {/* Layout de 2 columnas */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Columna Izquierda: Selección de Funcionario */}
                <div className='space-y-4'>
                  <div>
                    <Label className='text-sm font-medium mb-2 block'>Asignar a: <span className="text-red-500">*</span></Label>
                    <div className='border border-slate-200 rounded-lg max-h-[280px] overflow-y-auto'>
                      {usuariosAsignables.usuarios_area?.length > 0 && (
                        <div className='p-2 border-b border-slate-100'>
                          <p className='text-xs font-semibold text-slate-500 uppercase px-2 py-1 bg-slate-50 rounded'>Funcionarios del Área</p>
                          <div className='grid grid-cols-1 gap-1 mt-2'>
                            {usuariosAsignables.usuarios_area.map(u => (
                              <button
                                key={u.id}
                                type='button'
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${
                                  asignacion.responsable_id === u.id 
                                    ? 'bg-[#1E3649] text-white' 
                                    : 'hover:bg-slate-100 text-slate-700'
                                }`}
                                onClick={() => handleSelectUsuario(u.id)}
                              >
                                <User className='w-4 h-4 flex-shrink-0' />
                                <span className='truncate'>{u.name}</span>
                                <span className='text-xs opacity-70'>({u.cargo || u.role})</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {usuariosAsignables.otros_lideres?.length > 0 && (
                        <div className='p-2 border-b border-slate-100'>
                          <p className='text-xs font-semibold text-slate-500 uppercase px-2 py-1 bg-slate-50 rounded'>Otros Líderes</p>
                          <div className='grid grid-cols-1 gap-1 mt-2'>
                            {usuariosAsignables.otros_lideres.map(u => (
                              <button
                                key={u.id}
                                type='button'
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${
                                  asignacion.responsable_id === u.id 
                                    ? 'bg-[#1E3649] text-white' 
                                    : 'hover:bg-slate-100 text-slate-700'
                                }`}
                                onClick={() => handleSelectUsuario(u.id)}
                              >
                                <User className='w-4 h-4 flex-shrink-0' />
                                <span className='truncate'>{u.name}</span>
                                <span className='text-xs opacity-70'>(Líder)</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {usuariosAsignables.socios?.length > 0 && (
                        <div className='p-2'>
                          <p className='text-xs font-semibold text-slate-500 uppercase px-2 py-1 bg-slate-50 rounded'>Socios</p>
                          <div className='grid grid-cols-1 gap-1 mt-2'>
                            {usuariosAsignables.socios.map(u => (
                              <button
                                key={u.id}
                                type='button'
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${
                                  asignacion.responsable_id === u.id 
                                    ? 'bg-[#1E3649] text-white' 
                                    : 'hover:bg-slate-100 text-slate-700'
                                }`}
                                onClick={() => handleSelectUsuario(u.id)}
                              >
                                <Star className='w-4 h-4 flex-shrink-0' />
                                <span className='truncate'>{u.name}</span>
                                <span className='text-xs opacity-70'>(Socio)</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Columna Derecha: Fecha y Documentos */}
                <div className='space-y-4'>
                  {/* Info del plazo de la tarea específica */}
                  {plazoPspInfo && (
                    <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
                      <div className='flex items-center gap-2 text-blue-800 text-sm font-medium'>
                        <Clock className='w-4 h-4' />
                        <span>Plazo original de la tarea</span>
                      </div>
                      <p className='text-xs text-blue-600 mt-1'>
                        Fecha límite establecida en PSP: <strong>{plazoPspInfo.fecha_limite && new Date(plazoPspInfo.fecha_limite).toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                      </p>
                    </div>
                  )}
                  
                  {/* Fecha de Vencimiento */}
                  <div>
                    <Label className='text-sm font-medium mb-2 block'>
                      Fecha de Vencimiento <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type='date'
                      value={asignacion.nuevo_plazo}
                      onChange={(e) => handleCambioFechaPlazo(e.target.value)}
                      className={`${
                        plazoExcedePsp 
                          ? 'border-red-400 focus:border-red-500 bg-red-50' 
                          : !asignacion.nuevo_plazo 
                            ? 'border-amber-300 focus:border-amber-400' 
                            : 'border-slate-200'
                      }`}
                    />
                    {!asignacion.nuevo_plazo && (
                      <p className="text-xs text-amber-600 mt-1">Este campo es obligatorio al asignar</p>
                    )}
                    
                    {/* Alerta si el plazo excede el deadline de la tarea */}
                    {plazoExcedePsp && (
                      <div className='mt-3 p-3 bg-red-50 border-2 border-red-300 rounded-lg'>
                        <div className='flex items-start gap-2'>
                          <AlertTriangle className='w-5 h-5 text-red-600 flex-shrink-0 mt-0.5' />
                          <div className='flex-1'>
                            <p className='text-sm font-medium text-red-800'>
                              Atención: El plazo asignado excede el compromiso establecido para esta tarea
                            </p>
                            <p className='text-xs text-red-600 mt-1'>
                              La fecha límite de esta tarea era el <strong>{plazoPspInfo?.fecha_limite && new Date(plazoPspInfo.fecha_limite).toLocaleDateString('es-CL')}</strong>.
                            </p>
                            <div className='mt-2'>
                              <Label className='text-xs font-medium text-red-700'>
                                Justificación del retraso <span className='text-red-500'>*</span>
                              </Label>
                              <Textarea
                                value={justificacionPlazo}
                                onChange={(e) => setJustificacionPlazo(e.target.value)}
                                placeholder='Por favor, justifique el retraso o ajuste la fecha...'
                                className='mt-1 border-red-300 focus:border-red-400 text-sm'
                                rows={2}
                              />
                              <p className='text-xs text-red-500 mt-1'>
                                Este comentario quedará registrado para revisión de socios
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Selector de Prioridad */}
                  <div>
                    <Label className='text-sm font-medium mb-2 block'>Prioridad</Label>
                    <Select
                      value={String(asignacion.priority)}
                      onValueChange={(value) => setAsignacion({ ...asignacion, priority: parseInt(value) })}
                    >
                      <SelectTrigger data-testid="asignacion-prioridad">
                        <SelectValue placeholder="Seleccionar prioridad" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">
                          <span className="flex items-center gap-2">
                            <Flame className="w-4 h-4 text-red-500" />
                            <span>1 - Urgente (Prioritaria)</span>
                          </span>
                        </SelectItem>
                        <SelectItem value="2">
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 flex items-center justify-center text-blue-600 font-bold">●</span>
                            <span>2 - Regular (Normal)</span>
                          </span>
                        </SelectItem>
                        <SelectItem value="3">
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 flex items-center justify-center text-slate-400 font-bold">○</span>
                            <span>3 - Baja (Programable)</span>
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Por defecto: Regular. Solo cambie si es urgente o de baja prioridad.
                    </p>
                  </div>

                  {/* Sección de Adjuntos */}
                  <div className='border border-slate-200 rounded-lg p-4 bg-slate-50/50'>
                    <div className='flex items-center gap-2 mb-2'>
                      <Paperclip className='w-4 h-4 text-[#1E3649]' />
                      <Label className='text-sm font-medium text-slate-800'>Documentos Base</Label>
                    </div>
                    <p className='text-xs text-slate-500 mb-3'>
                      Adjunte documentos técnicos, instrucciones o archivos necesarios para la ejecución
                    </p>
                    <TaskAttachments
                      entityType="task"
                      entityId={selectedTarea.id}
                      attachments={adjuntosAsignacion}
                      onAttachmentsChange={setAdjuntosAsignacion}
                      canUpload={true}
                    />
                  </div>

                  {/* Comentarios al Asignado */}
                  <div className='border-2 border-blue-300 rounded-lg p-4 bg-blue-50/50'>
                    <div className='flex items-center gap-2 mb-2'>
                      <MessageSquare className='w-4 h-4 text-blue-700' />
                      <Label className='text-sm font-medium text-blue-800'>Comentarios al Asignado</Label>
                    </div>
                    <p className='text-xs text-blue-600 mb-3'>
                      Escriba instrucciones detalladas, notas de contexto o advertencias para el funcionario. Usa @ para mencionar.
                    </p>
                    <MentionInput
                      value={asignacion.comentario_inicial}
                      onChange={(val) => setAsignacion({ ...asignacion, comentario_inicial: val })}
                      onMentionsChange={setMencionesAsignacion}
                      placeholder="Instrucciones iniciales para el funcionario que recibirá el encargo..."
                      rows={4}
                      data-testid="comentario-asignado"
                    />
                  </div>
                </div>
              </div>

              {/* Botón de acción alineado a la derecha */}
              <div className='flex justify-end mt-6 pt-4 border-t border-slate-100'>
                <Button 
                  onClick={handleAsignarTarea} 
                  className='bg-[#1E3649] hover:bg-[#2A4A5F] text-white px-6'
                  disabled={!asignacion.responsable || !asignacion.nuevo_plazo || submitting}
                >
                  {submitting ? 'Asignando...' : 'Confirmar Asignación'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Revisar Tarea */}
      <Dialog open={showRevisar} onOpenChange={(open) => {
        setShowRevisar(open);
        if (!open) setAdjuntosRevision([]);
      }}>
        <DialogContent className='max-w-5xl w-[90vw] lg:w-[80vw] bg-white'>
          <DialogHeader>
            <DialogTitle className='text-lg font-semibold text-slate-900'>Revisar Tarea Completada</DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              El feedback y documentos adjuntos quedarán registrados en la bitácora
            </DialogDescription>
          </DialogHeader>
          {selectedTarea && (
            <div className='mt-4'>
              {/* Detalle de la tarea */}
              <div className='bg-green-50 border border-green-200 rounded-lg p-4 mb-6'>
                <p className='text-sm font-medium text-slate-800'>{selectedTarea.title}</p>
                <p className='text-xs text-slate-600 mt-1'>{selectedTarea.description}</p>
                <p className='text-xs text-green-700 mt-2 font-medium'>
                  Completada por: {selectedTarea.responsible}
                </p>
              </div>

              {/* Layout de 2 columnas */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Columna Izquierda: Documentos y Historial */}
                <div className='space-y-4'>
                  {/* Archivos Adjuntos completos */}
                  <div className='border border-slate-200 rounded-lg p-4'>
                    <p className='text-sm font-medium text-slate-700 mb-3'>Documentos de la Tarea</p>
                    <TaskAttachments
                      entityType="task"
                      entityId={selectedTarea.id}
                      attachments={selectedTarea.attachments || []}
                      canUpload={false}
                      showBitacoraAttachments={true}
                    />
                  </div>

                  {/* Bitácora - Historial de Comentarios */}
                  {selectedTarea.gestiones?.length > 0 && (
                    <div className='border rounded-lg p-4 bg-slate-50/50'>
                      <p className='text-sm font-medium text-slate-700 mb-3'>Historial de Comentarios</p>
                      <div className='max-h-40 overflow-y-auto space-y-2'>
                        {selectedTarea.gestiones.map((g, idx) => (
                          <div key={idx} className={`p-3 rounded-lg text-xs ${
                            g.tipo === 'completada' ? 'bg-blue-100 border border-blue-200' : 'bg-white border border-slate-100'
                          }`}>
                            <div className="flex justify-between items-center">
                              <span className='font-medium text-slate-800'>{g.usuario}</span>
                              <span className='text-slate-500'>{new Date(g.fecha).toLocaleString('es-CL')}</span>
                            </div>
                            <p className="text-slate-600 mt-1">{g.descripcion}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Columna Derecha: Feedback y Adjuntos de Revisión */}
                <div className='space-y-4'>
                  {/* Adjuntos del Líder para la revisión */}
                  <div className='border border-amber-200 rounded-lg p-4 bg-amber-50/30'>
                    <div className='flex items-center gap-2 mb-2'>
                      <Paperclip className='w-4 h-4 text-amber-600' />
                      <Label className='text-sm font-medium text-amber-800'>Adjuntar Feedback Visual</Label>
                    </div>
                    <p className='text-xs text-amber-600 mb-3'>
                      Documentos con correcciones marcadas o instrucciones adicionales
                    </p>
                    <TaskAttachments
                      entityType="task_review"
                      entityId={selectedTarea.id}
                      attachments={adjuntosRevision}
                      onAttachmentsChange={setAdjuntosRevision}
                      canUpload={true}
                    />
                  </div>

                  {/* Feedback */}
                  <div>
                    <Label className='text-sm font-medium mb-2 block'>
                      Feedback <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder='Su feedback quedará guardado en la bitácora para auditoría...'
                      rows={4}
                      className={`resize-none ${!feedback.trim() ? 'border-red-300 focus:border-red-400' : 'border-slate-200'}`}
                    />
                    {!feedback.trim() && (
                      <p className="text-xs text-red-500 mt-1">Este campo es obligatorio</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Botones de acción alineados a la derecha */}
              <div className='flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100'>
                <Button 
                  onClick={handleRechazar}
                  variant='outline'
                  className='text-red-600 border-red-300 hover:bg-red-50 px-4'
                  disabled={!feedback.trim() || submitting}
                >
                  <RotateCcw className='w-4 h-4 mr-1' />
                  Devolver
                </Button>
                <Button 
                  onClick={() => setShowEnviarSocios(true)}
                  variant='outline'
                  className='text-amber-600 border-amber-300 hover:bg-amber-50 px-4'
                  disabled={submitting}
                  data-testid="enviar-socios-btn"
                >
                  <Star className='w-4 h-4 mr-1' />
                  A Socios
                </Button>
                <Button 
                  onClick={handleAprobar}
                  className='bg-green-600 hover:bg-green-700 px-6'
                  disabled={!feedback.trim() || submitting}
                >
                  <ThumbsUp className='w-4 h-4 mr-1' />
                  Aprobar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Eliminación */}
      <Dialog open={showEliminar} onOpenChange={setShowEliminar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Eliminar Tarea
            </DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. ¿Está seguro de eliminar esta tarea?
            </DialogDescription>
          </DialogHeader>
          {tareaAEliminar && (
            <div className="mt-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-slate-900">{tareaAEliminar.title}</p>
                <p className="text-sm text-slate-600 mt-1">{tareaAEliminar.description?.slice(0, 100)}</p>
                <div className="flex gap-4 mt-2 text-xs text-slate-500">
                  <span>Área: {tareaAEliminar.area}</span>
                  {tareaAEliminar.responsible && tareaAEliminar.responsible !== 'Por asignar' && (
                    <span>Responsable: {tareaAEliminar.responsible}</span>
                  )}
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowEliminar(false);
                    setTareaAEliminar(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleEliminarTarea}
                  disabled={eliminando}
                  data-testid="confirmar-eliminar-btn"
                >
                  {eliminando ? 'Eliminando...' : 'Eliminar Tarea'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Crear Tarea */}
      <Dialog open={showCrearTarea} onOpenChange={(open) => {
        setShowCrearTarea(open);
        if (!open) {
          setSelectedPsp(null);
          setPspSearch('');
          setNuevaTarea({
            title: '',
            description: '',
            area: '',
            deadline: '',
            proposal_id: '',
            responsible: '',
            responsible_id: '',
            comentario_inicial: '',
            documentos_base: []
          });
          setMencionesNuevaTarea([]);
        }
      }}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <Plus className="w-5 h-5 text-[#1E3649]" />
              Crear Nueva Tarea
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Crea una tarea y asígnala a un miembro de tu equipo. Toda tarea debe estar vinculada a una PSP.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {/* Layout de 2 columnas */}
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              {/* Columna Izquierda: PSP y Detalles */}
              <div className="space-y-4">
                {/* Selector de PSP con búsqueda */}
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Cliente / PSP <span className="text-red-500">*</span></Label>
                  {selectedPsp ? (
                    <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                      <Briefcase className="w-5 h-5 text-[#1E3649]" />
                      <div className="flex-1">
                        <p className="font-medium text-sm text-slate-900">{selectedPsp.client_name}</p>
                        <p className="text-xs text-slate-600">PSP {selectedPsp.folio}: {selectedPsp.titulo?.slice(0, 40)}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          setSelectedPsp(null);
                          setNuevaTarea({ ...nuevaTarea, proposal_id: '' });
                        }}
                        className="text-slate-500 hover:text-slate-700"
                      >
                        Cambiar
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          value={pspSearch}
                          onChange={(e) => setPspSearch(e.target.value)}
                          placeholder="Buscar por cliente, folio o título de PSP..."
                          className="pl-10"
                          data-testid="psp-search-lider"
                        />
                      </div>
                      <div className="border rounded-lg max-h-32 overflow-y-auto">
                        {loadingPsps ? (
                          <div className="p-4 text-center text-slate-500 text-sm">
                            Cargando PSPs activas...
                          </div>
                        ) : pspsActivas.length === 0 ? (
                          <div className="p-4 text-center text-slate-500 text-sm">
                            {pspSearch ? 'No se encontraron PSPs' : 'No hay PSPs activas'}
                          </div>
                        ) : (
                          pspsActivas.map((psp) => (
                            <button
                              key={psp.id}
                              onClick={() => handleSelectPsp(psp)}
                              className="w-full text-left p-3 hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                            >
                              <p className="font-medium text-sm text-slate-900">{psp.client_name}</p>
                              <p className="text-xs text-slate-500">PSP {psp.folio}: {psp.titulo?.slice(0, 50)}</p>
                            </button>
                          ))
                        )}
                      </div>
                      {!nuevaTarea.proposal_id && (
                        <p className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Seleccione una PSP para vincular la tarea
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Título */}
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Título de la Tarea <span className="text-red-500">*</span></Label>
                  <Input
                    value={nuevaTarea.title}
                    onChange={(e) => setNuevaTarea({ ...nuevaTarea, title: e.target.value })}
                    placeholder="Ej: Revisar declaración de impuestos"
                    data-testid="nueva-tarea-titulo-lider"
                  />
                </div>

                {/* Descripción */}
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Descripción <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={nuevaTarea.description}
                    onChange={(e) => setNuevaTarea({ ...nuevaTarea, description: e.target.value })}
                    placeholder="Describe detalladamente la tarea a realizar..."
                    rows={3}
                    className='resize-none'
                    data-testid="nueva-tarea-descripcion-lider"
                  />
                </div>

                {/* Comentario Inicial / Instrucciones */}
                <div className="space-y-2 border border-blue-200 rounded-lg p-4 bg-blue-50/30">
                  <Label className='text-sm font-medium text-blue-800 flex items-center gap-2'>
                    <MessageSquare className="w-4 h-4" />
                    Instrucciones Iniciales (opcional)
                  </Label>
                  <MentionInput
                    value={nuevaTarea.comentario_inicial}
                    onChange={(val) => setNuevaTarea({ ...nuevaTarea, comentario_inicial: val })}
                    onMentionsChange={setMencionesNuevaTarea}
                    placeholder="Instrucciones o comentarios iniciales para el responsable... Usa @ para mencionar"
                    rows={3}
                    data-testid="nueva-tarea-instrucciones"
                  />
                </div>
              </div>

              {/* Columna Derecha: Área, Fecha, Asignación y Documentos */}
              <div className="space-y-4">
                {/* Área */}
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Área <span className="text-red-500">*</span></Label>
                  <Select
                    value={nuevaTarea.area}
                    onValueChange={(value) => {
                      setNuevaTarea({ ...nuevaTarea, area: value });
                      // Cargar usuarios de la nueva área seleccionada
                      fetchUsuariosAsignables(value);
                    }}
                  >
                    <SelectTrigger data-testid="nueva-tarea-area-lider">
                      <SelectValue placeholder="Seleccionar área" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Tributaria">Tributaria</SelectItem>
                      <SelectItem value="Legal">Legal</SelectItem>
                      <SelectItem value="Contabilidad">Contabilidad</SelectItem>
                      <SelectItem value="Comercial">Comercial</SelectItem>
                      <SelectItem value="Adm. y Finanzas">Adm. y Finanzas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Fecha de Vencimiento */}
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Fecha de Vencimiento <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={nuevaTarea.deadline}
                    onChange={(e) => setNuevaTarea({ ...nuevaTarea, deadline: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="nueva-tarea-deadline-lider"
                  />
                </div>

                {/* Asignar a */}
                <div className="space-y-2">
                  <Label className='text-sm font-medium'>Asignar a (opcional)</Label>
                  <Select
                    value={nuevaTarea.responsible_id || "sin-asignar"}
                    onValueChange={(value) => {
                      if (value === "sin-asignar") {
                        setNuevaTarea({ 
                          ...nuevaTarea, 
                          responsible_id: '',
                          responsible: ''
                        });
                      } else {
                        const usuario = [...(usuariosAsignables.usuarios_area || []), ...(usuariosAsignables.otros_lideres || []), ...(usuariosAsignables.socios || [])]
                          .find(u => u.id === value);
                        setNuevaTarea({ 
                          ...nuevaTarea, 
                          responsible_id: value,
                          responsible: usuario?.name || ''
                        });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="nueva-tarea-responsable-lider">
                      <SelectValue placeholder="Sin asignar (Pendiente)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sin-asignar">Sin asignar (Pendiente de Asignar)</SelectItem>
                      {usuariosAsignables.usuarios_area?.length > 0 && (
                        <>
                          <div className="px-2 py-1 text-xs text-slate-500 font-semibold">Equipo del Área</div>
                          {usuariosAsignables.usuarios_area.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                        </>
                      )}
                      {usuariosAsignables.otros_lideres?.length > 0 && (
                        <>
                          <div className="px-2 py-1 text-xs text-slate-500 font-semibold">Otros Líderes</div>
                          {usuariosAsignables.otros_lideres.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                        </>
                      )}
                      {usuariosAsignables.socios?.length > 0 && (
                        <>
                          <div className="px-2 py-1 text-xs text-slate-500 font-semibold">Socios</div>
                          {usuariosAsignables.socios.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Si no asigna, la tarea quedará "Pendiente de Asignar"
                  </p>
                </div>

                {/* Documentos Base */}
                <div className="space-y-2 border border-green-200 rounded-lg p-4 bg-green-50/30">
                  <Label className='text-sm font-medium text-green-800 flex items-center gap-2'>
                    <Paperclip className="w-4 h-4" />
                    Documentos Base (opcional)
                  </Label>
                  <p className="text-xs text-green-600 mb-2">
                    Adjunte documentos de referencia o material de trabajo inicial
                  </p>
                  <TaskAttachments
                    entityType="task"
                    entityId={`temp-${Date.now()}`}
                    attachments={nuevaTarea.documentos_base || []}
                    onAttachmentsChange={(newAttachments) => {
                      setNuevaTarea({ ...nuevaTarea, documentos_base: newAttachments });
                    }}
                    canUpload={true}
                  />
                </div>
              </div>
            </div>

            {/* Botones de acción alineados a la derecha */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
              <Button 
                variant="outline"
                onClick={() => setShowCrearTarea(false)}
                className='px-4'
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleCrearTarea} 
                className="bg-[#1E3649] hover:bg-[#2A4A5F] text-white px-6"
                disabled={creandoTarea || !nuevaTarea.title || !nuevaTarea.description || !nuevaTarea.area || !nuevaTarea.deadline || !nuevaTarea.proposal_id}
                data-testid="confirmar-crear-tarea-lider-btn"
              >
                {creandoTarea ? 'Creando...' : 'Crear Tarea'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Tarea */}
      <Dialog open={showEditar} onOpenChange={setShowEditar}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <Pencil className="w-5 h-5 text-[#1E3649]" />
              Editar Tarea
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Modifica los detalles de la tarea
            </DialogDescription>
          </DialogHeader>
          {tareaAEditar && (
            <div className="mt-4">
              {/* Layout de 2 columnas */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Columna Izquierda: Título y Descripción */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className='text-sm font-medium'>Título</Label>
                    <Input
                      value={editData.title}
                      onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                      data-testid="edit-tarea-title"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className='text-sm font-medium'>Descripción</Label>
                    <Textarea
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      rows={6}
                      className='resize-none'
                      data-testid="edit-tarea-description"
                    />
                  </div>
                </div>
                
                {/* Columna Derecha: Área y Fecha */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className='text-sm font-medium'>Área</Label>
                    <Select
                      value={editData.area}
                      onValueChange={(value) => setEditData({ ...editData, area: value })}
                    >
                      <SelectTrigger data-testid="edit-tarea-area">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Legal">Legal</SelectItem>
                        <SelectItem value="Tributaria">Tributaria</SelectItem>
                        <SelectItem value="Contabilidad">Contabilidad</SelectItem>
                        <SelectItem value="Comercial">Comercial</SelectItem>
                        <SelectItem value="Adm. y Finanzas">Adm. y Finanzas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className='text-sm font-medium'>Fecha de Vencimiento</Label>
                    <Input
                      type="date"
                      value={editData.deadline}
                      onChange={(e) => setEditData({ ...editData, deadline: e.target.value })}
                      data-testid="edit-tarea-deadline"
                    />
                  </div>
                </div>
              </div>
              
              {/* Botones de acción alineados a la derecha */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditar(false);
                    setTareaAEditar(null);
                  }}
                  className='px-4'
                >
                  Cancelar
                </Button>
                <Button
                  className="bg-[#1E3649] hover:bg-[#2A4A5F] text-white px-6"
                  onClick={handleGuardarEdicion}
                  disabled={editando}
                  data-testid="guardar-edicion-btn"
                >
                  {editando ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Enviar a Revisión de Socios */}
      <Dialog open={showEnviarSocios} onOpenChange={setShowEnviarSocios}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600 text-lg font-semibold">
              <Star className="w-5 h-5" />
              Enviar a Revisión de Socios
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              La tarea será escalada a los socios para su revisión directiva
            </DialogDescription>
          </DialogHeader>
          {selectedTarea && (
            <div className="mt-4">
              {/* Detalle de la tarea */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="font-medium text-slate-800">{selectedTarea.title}</p>
                <p className="text-sm text-slate-600 mt-1">{selectedTarea.description?.slice(0, 150)}...</p>
                <div className="flex gap-4 mt-2 text-xs text-slate-500">
                  <span>Responsable: {selectedTarea.responsible}</span>
                  <span>Área: {selectedTarea.area}</span>
                </div>
              </div>

              {/* Layout de 2 columnas */}
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Columna Izquierda: Info adicional */}
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 font-medium mb-2">¿Cuándo escalar a socios?</p>
                    <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                      <li>Decisiones estratégicas o de alto impacto</li>
                      <li>Conflictos que requieren intervención directiva</li>
                      <li>Casos complejos que exceden competencias del área</li>
                    </ul>
                  </div>
                </div>

                {/* Columna Derecha: Formulario */}
                <div className="space-y-4">
                  <div>
                    <Label className='text-sm font-medium mb-2 block'>
                      Motivo del escalamiento <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      value={comentarioSocios}
                      onChange={(e) => setComentarioSocios(e.target.value)}
                      placeholder="Explique por qué esta tarea requiere revisión directiva de los socios..."
                      rows={5}
                      className='resize-none'
                      data-testid="comentario-socios"
                    />
                  </div>
                </div>
              </div>
              
              {/* Botones alineados a la derecha */}
              <div className='flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100'>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEnviarSocios(false);
                    setComentarioSocios('');
                  }}
                  className='px-4'
                >
                  Cancelar
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white px-6"
                  onClick={handleEnviarSocios}
                  disabled={!comentarioSocios.trim() || enviandoSocios}
                  data-testid="confirmar-enviar-socios-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {enviandoSocios ? 'Enviando...' : 'Enviar a Socios'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Ver Documentos de Seguimiento Equipo */}
      <Dialog open={showDocsEquipo} onOpenChange={setShowDocsEquipo}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3649] text-lg font-semibold">
              <Paperclip className="w-5 h-5" />
              Documentos de la Tarea
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Historial completo de documentos adjuntados en el proceso
            </DialogDescription>
          </DialogHeader>
          {tareaDocsEquipo && (
            <div className="mt-4">
              {/* Detalle de la tarea */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-slate-800">{tareaDocsEquipo.title}</p>
                <p className="text-xs text-slate-600 mt-1">{tareaDocsEquipo.description?.slice(0, 150)}...</p>
                <div className="flex items-center gap-2 mt-2 text-xs">
                  {tareaDocsEquipo.client_name && (
                    <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                      {tareaDocsEquipo.client_name}
                    </span>
                  )}
                  {tareaDocsEquipo.psp_folio && (
                    <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                      PSP: {tareaDocsEquipo.psp_folio}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Componente de Adjuntos con historial completo */}
              <TaskAttachments
                entityType="task"
                entityId={tareaDocsEquipo.id}
                attachments={tareaDocsEquipo.attachments || []}
                canUpload={false}
                showBitacoraAttachments={true}
              />
              
              {/* Botón alineado a la derecha */}
              <div className='flex justify-end mt-6 pt-4 border-t border-slate-100'>
                <Button 
                  onClick={() => setShowDocsEquipo(false)}
                  variant="outline"
                  className='px-6'
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Ver Bitácora Completa (Seguimiento Equipo) */}
      <Dialog open={showBitacoraEquipo} onOpenChange={setShowBitacoraEquipo}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3649] text-lg font-semibold">
              <MessageSquare className="w-5 h-5" />
              Bitácora Completa
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Historial completo de gestiones y comentarios de la tarea
            </DialogDescription>
          </DialogHeader>
          {tareaBitacoraEquipo && (
            <div className="mt-4">
              {/* Detalle de la tarea */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-slate-800">{tareaBitacoraEquipo.title}</p>
                <p className="text-xs text-slate-600 mt-1">{tareaBitacoraEquipo.description}</p>
                <div className="flex items-center gap-3 mt-2 text-xs">
                  <span className={`px-2 py-0.5 rounded ${getStatusColor(tareaBitacoraEquipo.status)}`}>
                    {tareaBitacoraEquipo.status}
                  </span>
                  {tareaBitacoraEquipo.responsible && (
                    <span className="text-slate-600">Responsable: {tareaBitacoraEquipo.responsible}</span>
                  )}
                  <span className="text-slate-500">Vence: {new Date(tareaBitacoraEquipo.deadline).toLocaleDateString('es-CL')}</span>
                </div>
              </div>

              {/* Bitácora de Gestiones */}
              <div className="space-y-3">
                {(!tareaBitacoraEquipo.gestiones || tareaBitacoraEquipo.gestiones.length === 0) ? (
                  <p className="text-sm text-slate-500 text-center py-8">No hay gestiones registradas en esta tarea</p>
                ) : (
                  tareaBitacoraEquipo.gestiones.map((gestion, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${
                      gestion.tipo === 'completada' ? 'bg-blue-50 border-blue-200' :
                      gestion.tipo === 'aprobacion' ? 'bg-green-50 border-green-200' :
                      gestion.tipo === 'rechazo' ? 'bg-red-50 border-red-200' :
                      gestion.tipo === 'revision_socios' ? 'bg-amber-50 border-amber-200' :
                      'bg-white border-slate-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-slate-800">{gestion.usuario}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            gestion.tipo === 'completada' ? 'bg-blue-200 text-blue-800' :
                            gestion.tipo === 'aprobacion' ? 'bg-green-200 text-green-800' :
                            gestion.tipo === 'rechazo' ? 'bg-red-200 text-red-800' :
                            gestion.tipo === 'revision_socios' ? 'bg-amber-200 text-amber-800' :
                            'bg-slate-200 text-slate-800'
                          }`}>
                            {gestion.tipo || 'Gestión'}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {new Date(gestion.fecha).toLocaleString('es-CL')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">{gestion.descripcion}</p>
                      {gestion.attachments?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-200">
                          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                            <Paperclip className="w-3 h-3" />
                            Archivos adjuntos ({gestion.attachments.length})
                          </p>
                          <div className="space-y-1">
                            {gestion.attachments.map((attachment, attIdx) => {
                              const fileId = typeof attachment === 'string' ? attachment : attachment.id;
                              const fileName = typeof attachment === 'object' ? attachment.original_name : `archivo_${attIdx + 1}`;
                              return (
                                <div 
                                  key={attIdx}
                                  className="flex items-center justify-between p-2 bg-white rounded border border-slate-100 hover:border-slate-300 transition-colors group"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <File className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                    <span className="text-xs text-slate-700 truncate">{fileName}</span>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => handleDownloadBitacoraFile(fileId, fileName)}
                                    title="Descargar archivo"
                                  >
                                    <Download className="w-3 h-3" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div className='flex justify-end mt-6 pt-4 border-t border-slate-100'>
                <Button 
                  onClick={() => setShowBitacoraEquipo(false)}
                  variant="outline"
                  className='px-6'
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Editar Tarea (Seguimiento Equipo) */}
      <Dialog open={showEditarEquipo} onOpenChange={setShowEditarEquipo}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3649] text-lg font-semibold">
              <Pencil className="w-5 h-5" />
              Editar Tarea
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Modifica los detalles de la tarea desde Seguimiento de Equipo
            </DialogDescription>
          </DialogHeader>
          {tareaEditarEquipo && (
            <div className="mt-4">
              {/* Aviso de notificación al funcionario */}
              {tareaEditarEquipo.responsible && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-800">
                      <strong>{tareaEditarEquipo.responsible}</strong> será notificado de los cambios realizados
                    </span>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">Los cambios quedarán registrados en la bitácora de la tarea</p>
                </div>
              )}
              
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className='text-sm font-medium'>Título</Label>
                    <Input
                      value={editDataEquipo.title}
                      onChange={(e) => setEditDataEquipo({ ...editDataEquipo, title: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className='text-sm font-medium'>Descripción</Label>
                    <Textarea
                      value={editDataEquipo.description}
                      onChange={(e) => setEditDataEquipo({ ...editDataEquipo, description: e.target.value })}
                      rows={6}
                      className='resize-none'
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className='text-sm font-medium'>Área</Label>
                    <Select
                      value={editDataEquipo.area}
                      onValueChange={(value) => setEditDataEquipo({ ...editDataEquipo, area: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Legal">Legal</SelectItem>
                        <SelectItem value="Tributaria">Tributaria</SelectItem>
                        <SelectItem value="Contabilidad">Contabilidad</SelectItem>
                        <SelectItem value="Comercial">Comercial</SelectItem>
                        <SelectItem value="Adm. y Finanzas">Adm. y Finanzas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className='text-sm font-medium'>Fecha de Vencimiento</Label>
                    <Input
                      type="date"
                      value={editDataEquipo.deadline}
                      onChange={(e) => setEditDataEquipo({ ...editDataEquipo, deadline: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowEditarEquipo(false)} className='px-4'>
                  Cancelar
                </Button>
                <Button
                  className="bg-[#1E3649] hover:bg-[#2A4A5F] text-white px-6"
                  onClick={handleGuardarEdicionEquipo}
                  disabled={procesandoEquipo}
                >
                  {procesandoEquipo ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Reasignar Tarea (Seguimiento Equipo) */}
      <Dialog open={showReasignarEquipo} onOpenChange={(open) => {
        setShowReasignarEquipo(open);
        if (!open) setReasignacionEquipo({ responsable: '', responsable_id: '', nuevo_plazo: '', comentario: '' });
      }}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600 text-lg font-semibold">
              <UserPlus className="w-5 h-5" />
              Reasignar Tarea
            </DialogTitle>
            <DialogDescription className='text-sm text-slate-500'>
              Transfiere esta tarea a otro miembro del equipo
            </DialogDescription>
          </DialogHeader>
          {tareaReasignarEquipo && (
            <div className="mt-4">
              {/* Detalle de la tarea */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-slate-800">{tareaReasignarEquipo.title}</p>
                <p className="text-xs text-slate-600 mt-1">{tareaReasignarEquipo.description?.slice(0, 100)}...</p>
                <div className="flex gap-4 mt-2 text-xs text-slate-500">
                  <span>Actual: {tareaReasignarEquipo.responsible || 'Sin asignar'}</span>
                  <span>Área: {tareaReasignarEquipo.area}</span>
                </div>
              </div>

              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Columna Izquierda: Selector de Funcionario */}
                <div className='space-y-4'>
                  <div>
                    <Label className='text-sm font-medium mb-2 block'>Nuevo Responsable <span className="text-red-500">*</span></Label>
                    <div className='border border-slate-200 rounded-lg max-h-[280px] overflow-y-auto'>
                      {usuariosAsignables.usuarios_area?.length > 0 && (
                        <div className='p-2 border-b border-slate-100'>
                          <p className='text-xs font-semibold text-slate-500 uppercase px-2 py-1 bg-slate-50 rounded'>Funcionarios del Área</p>
                          <div className='grid grid-cols-1 gap-1 mt-2'>
                            {usuariosAsignables.usuarios_area.map(u => (
                              <button
                                key={u.id}
                                type='button'
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${
                                  reasignacionEquipo.responsable_id === u.id 
                                    ? 'bg-[#1E3649] text-white' 
                                    : 'hover:bg-slate-100 text-slate-700'
                                }`}
                                onClick={() => handleSelectUsuarioReasignar(u.id)}
                              >
                                <User className='w-4 h-4 flex-shrink-0' />
                                <span className='truncate'>{u.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {usuariosAsignables.otros_lideres?.length > 0 && (
                        <div className='p-2 border-b border-slate-100'>
                          <p className='text-xs font-semibold text-slate-500 uppercase px-2 py-1 bg-slate-50 rounded'>Otros Líderes</p>
                          <div className='grid grid-cols-1 gap-1 mt-2'>
                            {usuariosAsignables.otros_lideres.map(u => (
                              <button
                                key={u.id}
                                type='button'
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${
                                  reasignacionEquipo.responsable_id === u.id 
                                    ? 'bg-[#1E3649] text-white' 
                                    : 'hover:bg-slate-100 text-slate-700'
                                }`}
                                onClick={() => handleSelectUsuarioReasignar(u.id)}
                              >
                                <User className='w-4 h-4 flex-shrink-0' />
                                <span className='truncate'>{u.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {usuariosAsignables.socios?.length > 0 && (
                        <div className='p-2'>
                          <p className='text-xs font-semibold text-slate-500 uppercase px-2 py-1 bg-slate-50 rounded'>Socios</p>
                          <div className='grid grid-cols-1 gap-1 mt-2'>
                            {usuariosAsignables.socios.map(u => (
                              <button
                                key={u.id}
                                type='button'
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${
                                  reasignacionEquipo.responsable_id === u.id 
                                    ? 'bg-[#1E3649] text-white' 
                                    : 'hover:bg-slate-100 text-slate-700'
                                }`}
                                onClick={() => handleSelectUsuarioReasignar(u.id)}
                              >
                                <Star className='w-4 h-4 flex-shrink-0' />
                                <span className='truncate'>{u.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Columna Derecha: Fecha y Comentario */}
                <div className='space-y-4'>
                  <div>
                    <Label className='text-sm font-medium mb-2 block'>
                      Nuevo Plazo <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type='date'
                      value={reasignacionEquipo.nuevo_plazo}
                      onChange={(e) => setReasignacionEquipo({ ...reasignacionEquipo, nuevo_plazo: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className='text-sm font-medium mb-2 block'>Comentario (opcional)</Label>
                    <Textarea
                      value={reasignacionEquipo.comentario}
                      onChange={(e) => setReasignacionEquipo({ ...reasignacionEquipo, comentario: e.target.value })}
                      placeholder="Motivo de la reasignación o instrucciones adicionales..."
                      rows={4}
                      className='resize-none'
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowReasignarEquipo(false)} className='px-4'>
                  Cancelar
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                  onClick={handleReasignarEquipo}
                  disabled={!reasignacionEquipo.responsable_id || !reasignacionEquipo.nuevo_plazo || procesandoEquipo}
                >
                  {procesandoEquipo ? 'Reasignando...' : 'Confirmar Reasignación'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Credenciales del Cliente - Usando componente jerárquico */}
      <Dialog open={showCredencialesEquipo} onOpenChange={setShowCredencialesEquipo}>
        <DialogContent className="max-w-2xl w-[90vw] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700 text-lg font-semibold">
              <Key className="w-5 h-5" />
              Credenciales del Cliente
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Credenciales de acceso para {tareaCredencialesEquipo?.client_name || tareaCredencialesEquipo?.cliente_nombre}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <HierarchicalCredentials
              clientName={tareaCredencialesEquipo?.client_name || tareaCredencialesEquipo?.cliente_nombre}
              clientRut={tareaCredencialesEquipo?.client_rut}
              clientClaveSii={tareaCredencialesEquipo?.client_clave_sii}
              clientClaveUnica={tareaCredencialesEquipo?.client_clave_unica}
              empresas={tareaCredencialesEquipo?.client_empresas || []}
              defaultExpanded={true}
            />
            <div className="flex justify-end mt-6 pt-4 border-t border-slate-100">
              <Button variant="outline" onClick={() => setShowCredencialesEquipo(false)}>Cerrar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Crear Tarea Periódica */}
      <Dialog open={showCrearPeriodica} onOpenChange={setShowCrearPeriodica}>
        <DialogContent className="max-w-5xl w-[90vw] lg:w-[80vw] bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700 text-lg font-semibold">
              <RefreshCw className="w-5 h-5" />
              Crear Tarea Periódica
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Crear una tarea recurrente. Puede asignarla ahora o dejarla pendiente de asignar.
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Columna izquierda */}
              <div className="space-y-4">
                {/* Selector de Funcionario (Opcional) */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <Label className="text-sm font-medium text-slate-700">Asignar a Funcionario (opcional)</Label>
                  <Select
                    value={periodicaForm.asignado_id || "_none"}
                    onValueChange={(val) => setPeriodicaForm(prev => ({ ...prev, asignado_id: val === "_none" ? "" : val }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Sin asignar (quedará pendiente)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Sin asignar (pendiente)</SelectItem>
                      {funcionariosArea.map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.name} - {f.cargo || f.role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!periodicaForm.asignado_id && (
                    <p className="text-xs text-amber-600 mt-1">La tarea aparecerá en "Tareas por Asignar"</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-slate-700">Título de la Tarea *</Label>
                  <Input
                    value={periodicaForm.titulo}
                    onChange={(e) => setPeriodicaForm(prev => ({ ...prev, titulo: e.target.value }))}
                    placeholder="Ej: Revisión mensual de F29"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-slate-700">Descripción</Label>
                  <Textarea
                    value={periodicaForm.descripcion}
                    onChange={(e) => setPeriodicaForm(prev => ({ ...prev, descripcion: e.target.value }))}
                    placeholder="Descripción detallada..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
                
                {/* Checkbox Sin Cliente */}
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <input
                    type="checkbox"
                    id="sin-cliente-lider"
                    checked={periodicaForm.sin_cliente}
                    onChange={(e) => {
                      setPeriodicaForm(prev => ({ 
                        ...prev, 
                        sin_cliente: e.target.checked,
                        client_id: e.target.checked ? '' : prev.client_id,
                        psp_id: e.target.checked ? '' : prev.psp_id
                      }));
                      if (e.target.checked) setPspsCliente([]);
                    }}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                  <Label htmlFor="sin-cliente-lider" className="text-sm text-slate-700 cursor-pointer">
                    Sin cliente asociado
                  </Label>
                </div>
                
                {/* Cliente y PSP - Solo visible si no marcó "sin cliente" */}
                {!periodicaForm.sin_cliente && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Cliente <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={periodicaForm.client_id || "_none"}
                        onValueChange={(val) => handleClienteChangeLider(val === "_none" ? "" : val)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleccionar cliente" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Seleccionar cliente...</SelectItem>
                          {clientesLista.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">PSP del Cliente</Label>
                      <Select
                        value={periodicaForm.psp_id || "_none"}
                        onValueChange={(val) => setPeriodicaForm(prev => ({ ...prev, psp_id: val === "_none" ? "" : val }))}
                        disabled={!periodicaForm.client_id}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder={periodicaForm.client_id ? "Seleccionar PSP" : "Seleccione cliente primero"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Sin PSP</SelectItem>
                          {pspsCliente.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.folio}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {periodicaForm.client_id && pspsCliente.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">Este cliente no tiene PSPs activas</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Columna derecha - Programación */}
              <div className="space-y-4 bg-indigo-50/50 p-4 rounded-lg border border-indigo-100">
                <h3 className="font-semibold text-indigo-800 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Programación de Recurrencia
                </h3>
                
                <div>
                  <Label className="text-sm font-medium text-slate-700">Fecha de Inicio *</Label>
                  <Input
                    type="date"
                    value={periodicaForm.fecha_inicio ? (periodicaForm.fecha_inicio.includes('T') ? periodicaForm.fecha_inicio.split('T')[0] : periodicaForm.fecha_inicio) : ''}
                    onChange={(e) => {
                      const fechaValor = e.target.value;
                      if (fechaValor) {
                        setPeriodicaForm(prev => ({ ...prev, fecha_inicio: fechaValor + 'T09:00:00Z' }));
                      }
                    }}
                    className="mt-1"
                  />
                  {periodicaForm.fecha_inicio && (
                    <p className="text-xs text-green-600 mt-1">
                      Fecha seleccionada: {new Date(periodicaForm.fecha_inicio).toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  )}
                </div>
                
                <div>
                  <Label className="text-sm font-medium text-slate-700">Frecuencia</Label>
                  <Select
                    value={periodicaForm.frecuencia}
                    onValueChange={(val) => setPeriodicaForm(prev => ({ ...prev, frecuencia: val }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="diaria">Diaria</SelectItem>
                      <SelectItem value="semanal">Semanal</SelectItem>
                      <SelectItem value="mensual">Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {periodicaForm.frecuencia === 'semanal' && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Día de la Semana</Label>
                    <Select
                      value={periodicaForm.dia_semana?.toString() || '1'}
                      onValueChange={(val) => setPeriodicaForm(prev => ({ ...prev, dia_semana: parseInt(val) }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Lunes</SelectItem>
                        <SelectItem value="2">Martes</SelectItem>
                        <SelectItem value="3">Miércoles</SelectItem>
                        <SelectItem value="4">Jueves</SelectItem>
                        <SelectItem value="5">Viernes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {periodicaForm.frecuencia === 'mensual' && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Día del Mes</Label>
                    <Select
                      value={periodicaForm.dia_mes?.toString() || '1'}
                      onValueChange={(val) => setPeriodicaForm(prev => ({ ...prev, dia_mes: parseInt(val) }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(28)].map((_, i) => (
                          <SelectItem key={i + 1} value={(i + 1).toString()}>Día {i + 1}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <Label className="text-sm font-medium text-slate-700">Repeticiones (máx. 50)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={periodicaForm.repeticiones_total}
                    onChange={(e) => setPeriodicaForm(prev => ({ 
                      ...prev, 
                      repeticiones_total: Math.min(50, Math.max(1, parseInt(e.target.value) || 1)) 
                    }))}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Adjuntos */}
            <div className="border-t border-slate-200 pt-4">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Archivos Adjuntos (Opcional)</Label>
              <TaskAttachments
                entityType="tarea_periodica"
                entityId="new"
                attachments={adjuntosPeriodica}
                onAttachmentsChange={setAdjuntosPeriodica}
                canUpload={true}
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button variant="outline" onClick={() => setShowCrearPeriodica(false)}>Cancelar</Button>
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleCrearPeriodicaLider}
                disabled={!periodicaForm.titulo.trim() || !periodicaForm.fecha_inicio || (!periodicaForm.sin_cliente && !periodicaForm.client_id)}
                data-testid="crear-periodica-submit-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {periodicaForm.asignado_id ? 'Crear y Asignar' : 'Crear Tarea Periódica'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TableroLideres;
